import { useEffect, useMemo, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import {
  AlertTriangle,
  ArrowRight,
  Bell,
  BookOpen,
  BriefcaseBusiness,
  CalendarCheck,
  CheckCircle,
  ChevronDown,
  ChevronRight,
  ChevronUp,
  Clock,
  ClipboardCheck,
  ExternalLink,
  FileText,
  Gauge,
  History,
  Lightbulb,
  Loader2,
  Mail,
  MessageSquareText,
  MoreHorizontal,
  RefreshCw,
  Search,
  Shield,
  Sparkles,
  StickyNote,
  Target,
  TrendingUp,
  UserRound,
  Wand2,
} from 'lucide-react';
import { getCyberAdvisorDashboardData } from '../services/cyberAdvisorDashboardService';
import { createUsecureFollowupProjectTask } from '../services/usecureService';
import AIAssistantModal from './AIAssistantModal';
import { useAuth } from '../contexts/AuthContext';
import {
  DEFAULT_CYBER_ADVISOR_DASHBOARD_STATE,
  appendCyberAdvisorDashboardHistory,
  getCyberAdvisorDashboardState,
  saveCyberAdvisorDashboardState,
} from '../services/cyberAdvisorDashboardPersistenceService';
import type {
  CyberAdvisorDailyPlan,
  CyberAdvisorDashboardHistoryEvent,
  CyberAdvisorScopedState,
  CyberAdvisorItemWorkflowStatus,
} from '../services/cyberAdvisorDashboardPersistenceService';
import type {
  AdvisorDashboardBucket,
  AdvisorDashboardData,
  AdvisorDashboardItem,
  AdvisorDashboardSource,
} from '../services/cyberAdvisorDashboardService';

interface CyberAdvisorDashboardModalProps {
  isOpen: boolean;
  onClose: () => void;
  onOpenUsecure: () => void;
  onOpenOffers: () => void;
}

type PriorityFilter = 'all' | 'high';
type SourceFilter = 'all' | AdvisorDashboardSource;
type AdvisorViewMode = 'actions' | 'suivis' | 'assigner';
type AdvisorScope = 'me' | 'all' | 'advisor';
type HorizonFilter = 'today' | 'week' | 'watch' | 'waiting' | 'inbox';
type SnoozeUntil = Record<string, string>;
type NotesByClient = Record<string, string>;
type ItemStatuses = Record<string, CyberAdvisorItemWorkflowStatus>;
type AdvisorHistoryEvent = CyberAdvisorDashboardHistoryEvent;
type DayPlanDraftDecision = 'scheduled' | 'tomorrow' | 'removed';
type DayPlanDraftRow = { itemId: string; durationMinutes: number; decision: DayPlanDraftDecision };
const FALLBACK_STORAGE_KEY = 'prisme.cyberAdvisorDashboard.fallbackState';
const FULL_DAY_PLAN_LIMIT = 10;
const LUNCH_BREAK_START = '12:00';
const AFTERNOON_START = '13:00';
const DAY_END = '16:30';

const advisorDashboardBlocks: Array<{
  title: string;
  bucket: AdvisorDashboardBucket;
  icon: typeof CalendarCheck;
  tone: 'blue' | 'amber' | 'indigo' | 'slate';
  description: string;
}> = [
  {
    title: 'À traiter aujourd’hui',
    bucket: 'today',
    icon: CalendarCheck,
    tone: 'blue',
    description: 'File de travail priorisée : suivis, tâches, offres et validations qui demandent une action.',
  },
  {
    title: 'Clients à risque / à relancer',
    bucket: 'risk',
    icon: AlertTriangle,
    tone: 'amber',
    description: 'Signaux faibles, relances et incohérences à traiter avant que les dossiers stagnent.',
  },
  {
    title: 'Briefings prêts à générer',
    bucket: 'briefing',
    icon: MessageSquareText,
    tone: 'indigo',
    description: 'Préparation conseiller : à savoir, à dire au client, décisions à obtenir.',
  },
  {
    title: 'Documents à produire',
    bucket: 'documents',
    icon: FileText,
    tone: 'slate',
    description: 'Rapports, ODS et livrables qui peuvent être préparés ou révisés.',
  },
];

const toneClasses = {
  blue: { shell: 'border-blue-100 bg-blue-50', icon: 'bg-blue-600 text-white', pill: 'bg-blue-100 text-blue-800' },
  amber: { shell: 'border-amber-100 bg-amber-50', icon: 'bg-amber-500 text-white', pill: 'bg-amber-100 text-amber-800' },
  indigo: { shell: 'border-indigo-100 bg-indigo-50', icon: 'bg-indigo-600 text-white', pill: 'bg-indigo-100 text-indigo-800' },
  slate: { shell: 'border-slate-200 bg-slate-50', icon: 'bg-slate-800 text-white', pill: 'bg-slate-200 text-slate-800' },
};

const workflowStatuses: Array<{ value: CyberAdvisorItemWorkflowStatus; label: string; className: string }> = [
  { value: 'a_traiter', label: 'À traiter', className: 'bg-slate-100 text-slate-700 border-slate-200' },
  { value: 'en_attente_client', label: 'En attente client', className: 'bg-amber-50 text-amber-800 border-amber-100' },
  { value: 'a_valider', label: 'À valider', className: 'bg-blue-50 text-blue-800 border-blue-100' },
  { value: 'briefing_genere', label: 'Briefing généré', className: 'bg-indigo-50 text-indigo-800 border-indigo-100' },
  { value: 'donnee_corrigee', label: 'Donnée corrigée', className: 'bg-emerald-50 text-emerald-800 border-emerald-100' },
  { value: 'pas_aujourdhui', label: 'Pas aujourd’hui', className: 'bg-slate-100 text-slate-700 border-slate-200' },
  { value: 'traite', label: 'Traité', className: 'bg-emerald-600 text-white border-emerald-600' },
];

function workflowStatusMeta(status?: CyberAdvisorItemWorkflowStatus) {
  return workflowStatuses.find((candidate) => candidate.value === status) || workflowStatuses[0];
}


function currentPeriodMonth() {
  return new Date().toISOString().slice(0, 7);
}

function tomorrowIso() {
  const date = new Date();
  date.setDate(date.getDate() + 1);
  return date.toISOString().slice(0, 10);
}

function nextWeekIso() {
  const date = new Date();
  date.setDate(date.getDate() + 7);
  return date.toISOString().slice(0, 10);
}

function loadFallbackState() {
  try {
    const raw = window.localStorage.getItem(FALLBACK_STORAGE_KEY);
    return raw ? { ...DEFAULT_CYBER_ADVISOR_DASHBOARD_STATE, ...JSON.parse(raw) } : DEFAULT_CYBER_ADVISOR_DASHBOARD_STATE;
  } catch {
    return DEFAULT_CYBER_ADVISOR_DASHBOARD_STATE;
  }
}

function saveFallbackState(state: typeof DEFAULT_CYBER_ADVISOR_DASHBOARD_STATE) {
  try {
    window.localStorage.setItem(FALLBACK_STORAGE_KEY, JSON.stringify(state));
  } catch {
    // Le fallback local ne doit jamais bloquer le bureau conseiller.
  }
}

function sourceLabel(source: AdvisorDashboardSource) {
  return { usecure: 'uSecure', offers: 'Offres', projects: 'Projets', documents: 'Documents' }[source];
}


function normalizeAdvisor(value?: string | null) {
  return (value || '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[^a-z0-9@.]+/g, ' ')
    .trim();
}

function advisorMatches(owner: string | null | undefined, advisor: string) {
  const normalizedOwner = normalizeAdvisor(owner);
  const normalizedAdvisor = normalizeAdvisor(advisor);
  if (!normalizedOwner || !normalizedAdvisor) return false;
  const advisorParts = normalizedAdvisor.split(/\s+/).filter((part) => part.length > 1);
  return normalizedOwner.includes(normalizedAdvisor) || advisorParts.some((part) => normalizedOwner.includes(part));
}

function priorityLabel(priority: AdvisorDashboardItem['priority']) {
  if (priority === 'high') return 'Priorité élevée';
  if (priority === 'medium') return 'Priorité moyenne';
  return 'À surveiller';
}

function priorityClasses(priority: AdvisorDashboardItem['priority']) {
  if (priority === 'high') return 'border-red-100 bg-red-50 text-red-800';
  if (priority === 'medium') return 'border-amber-100 bg-amber-50 text-amber-800';
  return 'border-slate-200 bg-slate-50 text-slate-700';
}

function priorityScore(priority: AdvisorDashboardItem['priority']) {
  if (priority === 'high') return 3;
  if (priority === 'medium') return 2;
  return 1;
}

function estimatedMinutes(item: AdvisorDashboardItem) {
  const text = `${item.title} ${item.signal} ${item.nextAction}`.toLowerCase();
  if (text.includes('rapport') || text.includes('politique') || text.includes('livrable')) return item.priority === 'high' ? 45 : 30;
  if (text.includes('comité') || text.includes('briefing') || text.includes('rencontre')) return 30;
  if (text.includes('courriel') || text.includes('relance') || text.includes('envoyer')) return 20;
  if (text.includes('tâche') || text.includes('ouvrir')) return 15;
  return item.priority === 'high' ? 45 : item.priority === 'medium' ? 30 : 15;
}

function formatEffort(minutes: number) {
  if (minutes < 60) return `${minutes} min`;
  const hours = Math.floor(minutes / 60);
  const remaining = minutes % 60;
  return remaining ? `${hours}h ${remaining.toString().padStart(2, '0')}` : `${hours}h`;
}

function clientInitials(clientName: string) {
  const words = clientName.trim().split(/\s+/).filter(Boolean);
  if (!words.length) return 'CL';
  if (words.length === 1) return words[0].slice(0, 2).toUpperCase();
  return `${words[0][0]}${words[1][0]}`.toUpperCase();
}

function mandateProgress(item: AdvisorDashboardItem, index: number) {
  const base = item.bucket === 'documents' ? 85 : item.bucket === 'briefing' ? 65 : item.bucket === 'risk' ? 45 : 55;
  const priorityPenalty = item.priority === 'high' ? 15 : item.priority === 'medium' ? 5 : 0;
  return Math.max(20, Math.min(95, base - priorityPenalty + (index % 3) * 10));
}

function actionTone(item: AdvisorDashboardItem) {
  if (item.priority === 'high') return 'bg-red-600 hover:bg-red-700 text-white';
  if (item.priority === 'medium') return 'bg-amber-500 hover:bg-amber-600 text-white';
  return 'bg-blue-600 hover:bg-blue-700 text-white';
}

function isStillSnoozed(itemId: string, snoozedUntil: SnoozeUntil) {
  const until = snoozedUntil[itemId];
  if (!until) return false;
  return until >= new Date().toISOString().slice(0, 10);
}

function buildBriefingText(item: AdvisorDashboardItem, note?: string) {
  return [
    `Client : ${item.clientName}`,
    '',
    'À savoir',
    `- ${item.signal}`,
    ...item.why.slice(0, 4).map((reason) => `- ${reason}`),
    '',
    'Action recommandée',
    `- ${item.nextAction}`,
    '',
    'À dire au client',
    '- Nous avons identifié un point de suivi concret; valider le contexte, confirmer la prochaine étape et documenter la décision.',
    note ? `- Note conseiller : ${note}` : '',
  ].filter(Boolean).join('\n');
}


function addMinutesToTime(time: string, minutes: number) {
  const [hours, mins] = time.split(':').map(Number);
  const date = new Date();
  date.setHours(hours || 9, mins || 0, 0, 0);
  date.setMinutes(date.getMinutes() + minutes);
  return date.toTimeString().slice(0, 5);
}

function buildCalendarBlocks(items: AdvisorDashboardItem[], durationOverrides: Record<string, number> = {}) {
  let cursor = '09:00';
  return items.slice(0, FULL_DAY_PLAN_LIMIT).map((item, index) => {
    const durationMinutes = Math.min(90, Math.max(15, durationOverrides[item.id] || estimatedMinutes(item)));
    if (cursor < LUNCH_BREAK_START && addMinutesToTime(cursor, durationMinutes) > LUNCH_BREAK_START) cursor = AFTERNOON_START;
    if (cursor >= LUNCH_BREAK_START && cursor < AFTERNOON_START) cursor = AFTERNOON_START;
    const startTime = cursor;
    const endTime = addMinutesToTime(startTime, durationMinutes);
    cursor = addMinutesToTime(endTime, 10);
    if (cursor < LUNCH_BREAK_START && addMinutesToTime(cursor, 15) > LUNCH_BREAK_START) cursor = AFTERNOON_START;
    if (cursor >= DAY_END) cursor = addMinutesToTime(cursor, 10);
    return {
      id: `${item.id}-${startTime}`,
      startTime,
      endTime,
      label: index === 0 ? 'Priorité #1' : item.bucket === 'briefing' ? 'Briefing' : item.bucket === 'documents' ? 'Document' : item.bucket === 'risk' ? 'Risque' : 'Action',
      clientName: item.clientName,
      itemId: item.id,
      action: item.nextAction || item.title,
      durationMinutes,
    };
  });
}

function buildDailyEmailDraft(advisorName: string, items: AdvisorDashboardItem[], issueCount: number, estimatedTotal: number) {
  const subject = `Feuille de route conseiller — ${new Date().toLocaleDateString('fr-CA')}`;
  const lines = [
    `Bonjour${advisorName ? ` ${advisorName}` : ''},`,
    '',
    `Voici la feuille de route proposée pour aujourd’hui (${formatEffort(estimatedTotal)} estimé).`,
    '',
    'Priorités :',
    ...items.slice(0, 5).map((item, index) => `${index + 1}. ${item.clientName} — ${item.nextAction || item.title}`),
    '',
    issueCount ? `${issueCount} donnée(s) ou blocage(s) à corriger avant d’avancer proprement.` : 'Aucun blocage majeur détecté dans la vue actuelle.',
    '',
    'À valider en fin de journée : statuts, reports, notes client et prochaines actions.',
  ];
  return { subject, body: lines.join('\n') };
}

function buildPriorityRationale(items: AdvisorDashboardItem[]) {
  return items.slice(0, 5).map((item, index) => {
    const reasons = item.why.length ? item.why.slice(0, 2).join('; ') : item.signal;
    return `${index + 1}. ${item.clientName} — ${priorityLabel(item.priority)} · ${reasons}`;
  });
}

function buildIcs(plan: CyberAdvisorDailyPlan) {
  const today = new Date().toISOString().slice(0, 10).replace(/-/g, '');
  const stamp = new Date().toISOString().replace(/[-:]/g, '').split('.')[0] + 'Z';
  const blocks = plan.calendarBlocks || [];
  return ['BEGIN:VCALENDAR', 'VERSION:2.0', 'PRODID:-//PRIsME//Bureau conseiller//FR', ...blocks.flatMap((block) => {
    const start = `${today}T${block.startTime.replace(':', '')}00`;
    const end = `${today}T${block.endTime.replace(':', '')}00`;
    const summary = `${block.label} — ${block.clientName || 'Bureau conseiller'}`.replace(/,/g, '\,');
    const description = block.action.replace(/,/g, '\,').replace(/\n/g, '\\n');
    return ['BEGIN:VEVENT', `UID:${block.id}@prisme-bureau-conseiller`, `DTSTAMP:${stamp}`, `DTSTART:${start}`, `DTEND:${end}`, `SUMMARY:${summary}`, `DESCRIPTION:${description}`, 'END:VEVENT'];
  }), 'END:VCALENDAR'].join('\r\n');
}

export default function CyberAdvisorDashboardModal({ isOpen, onClose, onOpenUsecure, onOpenOffers }: CyberAdvisorDashboardModalProps) {
  const { user, isPrismeAdmin } = useAuth();
  const [data, setData] = useState<AdvisorDashboardData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sourceFilter, setSourceFilter] = useState<SourceFilter>('all');
  const [priorityFilter, setPriorityFilter] = useState<PriorityFilter>('all');
  const [search, setSearch] = useState('');
  const [viewMode, setViewMode] = useState<AdvisorViewMode>('actions');
  const [horizonFilter, setHorizonFilter] = useState<HorizonFilter>('today');
  const [advisorScope, setAdvisorScope] = useState<AdvisorScope>('me');
  const [selectedAdvisor, setSelectedAdvisor] = useState('');
  const [includeUnassigned, setIncludeUnassigned] = useState(false);
  const [coordinatorEdit, setCoordinatorEdit] = useState(false);
  const [mineOnly, setMineOnly] = useState(false);
  const [advisorName, setAdvisorName] = useState('');
  const [selectedItemId, setSelectedItemId] = useState<string | null>(null);
  const [snoozedUntil, setSnoozedUntil] = useState<SnoozeUntil>({});
  const [completedItems, setCompletedItems] = useState<Record<string, string>>({});
  const [notesByClient, setNotesByClient] = useState<NotesByClient>({});
  const [itemStatuses, setItemStatuses] = useState<ItemStatuses>({});
  const [dailyPlan, setDailyPlan] = useState<CyberAdvisorDailyPlan | null>(null);
  const [stateByAdvisor, setStateByAdvisor] = useState<Record<string, CyberAdvisorScopedState>>({});
  const [historyEvents, setHistoryEvents] = useState<AdvisorHistoryEvent[]>([]);
  const [persistenceStatus, setPersistenceStatus] = useState<'loading' | 'server' | 'fallback'>('loading');
  const [queueMode, setQueueMode] = useState(false);
  const [filtersCollapsed, setFiltersCollapsed] = useState(true);
  const [preparedPlanAt, setPreparedPlanAt] = useState<string | null>(null);
  const [dayPlanningOverlayOpen, setDayPlanningOverlayOpen] = useState(false);
  const [dayPlanDraftRows, setDayPlanDraftRows] = useState<DayPlanDraftRow[]>([]);
  const [draftIncludeFocus, setDraftIncludeFocus] = useState(true);
  const [draftIncludeRelances, setDraftIncludeRelances] = useState(true);
  const [draftIncludeCorrections, setDraftIncludeCorrections] = useState(false);
  const [draftBriefingBody, setDraftBriefingBody] = useState('');
  const [dailyApprovalState, setDailyApprovalState] = useState<'proposed' | 'approved'>(dailyPlan ? 'approved' : 'proposed');
  const [contextDrawerItem, setContextDrawerItem] = useState<AdvisorDashboardItem | null>(null);
  const [managementTab, setManagementTab] = useState<'attentes' | 'inbox' | 'sources'>('attentes');
  const [isEdithOpen, setIsEdithOpen] = useState(false);
  const [edithQuestion, setEdithQuestion] = useState<string | null>(null);
  const selectedPanelRef = useRef<HTMLDivElement | null>(null);
  const fileSectionRef = useRef<HTMLElement | null>(null);

  const scrollToFocus = () => {
    setViewMode('actions');
    window.requestAnimationFrame(() => {
      selectedPanelRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  };

  const scrollToFullFile = () => {
    setViewMode('suivis');
    setHorizonFilter('week');
    window.requestAnimationFrame(() => {
      fileSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  };

  const openItemDetails = (itemId: string) => {
    setSelectedItemId(itemId);
    window.requestAnimationFrame(() => {
      selectedPanelRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    });
  };

  const openSourceUrl = (item: AdvisorDashboardItem) => {
    const firstUrl = item.sourceUrl || item.actions.find((action) => action.href)?.href;
    if (firstUrl) {
      window.open(firstUrl, '_blank', 'noopener,noreferrer');
      addHistory('Source ouverte', item, firstUrl);
      return true;
    }
    addHistory('Source non liée — dossier affiché', item, 'Aucun lien source direct disponible pour cette action.');
    openItemDetails(item.id);
    return false;
  };

  const openEdithAssistant = (question: string, item?: AdvisorDashboardItem) => {
    if (item) openItemDetails(item.id);
    setEdithQuestion(question);
    setIsEdithOpen(true);
    addHistory('Question envoyée à Edith', item, question);
  };

  const advisorContextPrompt = (request: string, item?: AdvisorDashboardItem) => {
    const target = item || selectedItem || primaryFocus || visibleItems[0];
    const activeItems = visibleItems.slice(0, 6).map((candidate, index) => (
      `${index + 1}. ${candidate.clientName} — ${candidate.nextAction} (${priorityLabel(candidate.priority)}, ${sourceLabel(candidate.source)}) · Signal: ${candidate.signal}`
    )).join('\n');
    return [
      `Contexte: Bureau du conseiller cyber PRIsME/Edith.`,
      `Demande: ${request}`,
      target ? `Client ciblé: ${target.clientName}\nAction recommandée: ${target.nextAction}\nSignal: ${target.signal}\nPourquoi: ${(target.why.length ? target.why : [target.signal]).join('; ')}\nSource: ${sourceLabel(target.source)}\nPriorité: ${priorityLabel(target.priority)}\nStatut source: ${target.statusLabel || 'n/d'}\nStatut conseiller: ${workflowStatusMeta(itemStatuses[target.id]).label}\nNote conseiller: ${notesByClient[target.clientName] || 'aucune'}` : 'Client ciblé: aucun item sélectionné.',
      activeItems ? `File active:\n${activeItems}` : 'File active vide.',
      `Réponds en français naturel BZ, court et actionnable. Donne les prochaines actions concrètes, sans inventer de données absentes.`,
    ].join('\n\n');
  };

  const currentPersistentState = () => ({
    advisorName,
    snoozedUntil,
    completedItems,
    notesByClient,
    itemStatuses,
    dailyPlan,
    historyEvents,
    stateByAdvisor: {
      ...stateByAdvisor,
      [normalizeAdvisor(activeAdvisorName || currentAdvisorName || user?.email || 'global')]: { snoozedUntil, completedItems, notesByClient, itemStatuses, dailyPlan, historyEvents },
    },
  });

  const applyPersistentState = (state: typeof DEFAULT_CYBER_ADVISOR_DASHBOARD_STATE) => {
    const nextStateByAdvisor = state.stateByAdvisor || {};
    const advisorKey = normalizeAdvisor(activeAdvisorName || currentAdvisorName || user?.email || state.advisorName || 'global');
    const scoped = nextStateByAdvisor[advisorKey];
    setStateByAdvisor(nextStateByAdvisor);
    setAdvisorName(state.advisorName || currentAdvisorName || '');
    setSnoozedUntil(scoped?.snoozedUntil || state.snoozedUntil || {});
    setCompletedItems(scoped?.completedItems || state.completedItems || {});
    setNotesByClient(scoped?.notesByClient || state.notesByClient || {});
    setItemStatuses(scoped?.itemStatuses || state.itemStatuses || {});
    setDailyPlan(scoped?.dailyPlan || state.dailyPlan || null);
    setHistoryEvents((scoped?.historyEvents || state.historyEvents || []).slice(0, 50));
  };

  const persistStatePatch = async (patch: Partial<typeof DEFAULT_CYBER_ADVISOR_DASHBOARD_STATE>) => {
    const optimisticState = { ...currentPersistentState(), ...patch };
    saveFallbackState(optimisticState);
    try {
      const saved = await saveCyberAdvisorDashboardState(optimisticState);
      setPersistenceStatus('server');
      applyPersistentState(saved);
    } catch (_err) {
      setPersistenceStatus('fallback');
    }
  };

  const addHistory = (label: string, item?: AdvisorDashboardItem, detail?: string) => {
    const event: AdvisorHistoryEvent = {
      id: `${Date.now()}-${Math.random().toString(36).slice(2)}`,
      at: new Date().toISOString(),
      label,
      clientName: item?.clientName,
      detail,
    };
    const optimisticHistory = [event, ...historyEvents].slice(0, 50);
    setHistoryEvents(optimisticHistory);
    saveFallbackState({ ...currentPersistentState(), historyEvents: optimisticHistory });
    void saveCyberAdvisorDashboardState({ ...currentPersistentState(), historyEvents: optimisticHistory })
      .then((saved) => {
        setPersistenceStatus('server');
        applyPersistentState(saved);
      })
      .catch(() => setPersistenceStatus('fallback'));
  };

  const loadDashboard = async () => {
    setLoading(true);
    setError(null);
    try {
      const next = await getCyberAdvisorDashboardData(currentPeriodMonth());
      setData(next);
      setSelectedItemId((current) => current && next.items.some((item) => item.id === current) ? current : next.items[0]?.id || null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Impossible de charger le bureau du conseiller');
    } finally {
      setLoading(false);
    }
  };

  const loadPersistentState = async () => {
    setPersistenceStatus('loading');
    try {
      const state = await getCyberAdvisorDashboardState();
      applyPersistentState(state);
      setPersistenceStatus('server');
    } catch (_err) {
      applyPersistentState(loadFallbackState());
      setPersistenceStatus('fallback');
    }
  };

  useEffect(() => {
    if (isOpen && !data && !loading) void loadDashboard();
    if (isOpen) void loadPersistentState();
  }, [isOpen]);

  useEffect(() => {
    if (!user?.name) return;
    setSelectedAdvisor((current) => current || user.name);
    setAdvisorName((current) => current || user.name);
  }, [user?.name]);

  useEffect(() => {
    if (dailyPlan?.preparedAt) setDailyApprovalState('approved');
  }, [dailyPlan?.preparedAt]);

  const currentAdvisorName = user?.name || advisorName || '';
  const advisorOptions = useMemo(() => {
    const names = new Set<string>();
    if (currentAdvisorName) names.add(currentAdvisorName);
    for (const item of data?.items || []) {
      if (item.owner) names.add(item.owner);
    }
    return Array.from(names).sort((a, b) => a.localeCompare(b, 'fr'));
  }, [currentAdvisorName, data]);

  const activeAdvisorName = advisorScope === 'me' ? currentAdvisorName : advisorScope === 'advisor' ? selectedAdvisor : '';
  const isViewingOtherAdvisor = advisorScope === 'advisor' && Boolean(activeAdvisorName) && !advisorMatches(activeAdvisorName, currentAdvisorName);
  const isReadOnlyAdvisorView = (advisorScope === 'all' || isViewingOtherAdvisor) && !coordinatorEdit && !isPrismeAdmin;
  const canEditWorkflow = !isReadOnlyAdvisorView;
  const assignableItems = useMemo(() => (data?.items || []).filter((item) => !item.owner), [data]);
  const scopedItems = useMemo(() => {
    const rawItems = data?.items || [];
    if (advisorScope === 'all') return rawItems;
    if (!activeAdvisorName) return rawItems;
    return rawItems.filter((item) => advisorMatches(item.owner, activeAdvisorName) || (includeUnassigned && !item.owner));
  }, [activeAdvisorName, advisorScope, data, includeUnassigned]);

  const inclusionReason = (item: AdvisorDashboardItem) => {
    if (!item.owner) return 'Non assigné — à corriger';
    if (activeAdvisorName && advisorMatches(item.owner, activeAdvisorName)) return `Assigné à ${item.owner}`;
    return `Responsable : ${item.owner}`;
  };

  const visibleItems = useMemo(() => {
    const rawItems = scopedItems;
    const searchTerm = search.trim().toLowerCase();
    const mine = advisorName.trim().toLowerCase();
    return rawItems
      .filter((item) => !completedItems[item.id] && !['traite', 'pas_aujourdhui'].includes(itemStatuses[item.id] || ''))
      .filter((item) => !isStillSnoozed(item.id, snoozedUntil))
      .filter((item) => {
        const text = `${item.title} ${item.signal} ${item.nextAction} ${item.statusLabel || ''}`.toLowerCase();
        if (horizonFilter === 'today') return item.priority === 'high' || item.bucket === 'today';
        if (horizonFilter === 'week') return item.priority !== 'low' || item.bucket === 'briefing' || item.bucket === 'documents';
        if (horizonFilter === 'watch') return item.priority === 'low' || item.source === 'offers';
        if (horizonFilter === 'waiting') return text.includes('attente') || text.includes('relance');
        if (horizonFilter === 'inbox') return !item.owner || !item.nextAction || text.includes('manquante') || text.includes('bloqu');
        return true;
      })
      .filter((item) => sourceFilter === 'all' || item.source === sourceFilter)
      .filter((item) => priorityFilter !== 'high' || item.priority === 'high')
      .filter((item) => !mineOnly || !mine || (item.owner || '').toLowerCase().includes(mine))
      .filter((item) => {
        if (!searchTerm) return true;
        return [item.clientName, item.title, item.signal, item.nextAction, item.owner || '', item.statusLabel || '']
          .some((value) => value.toLowerCase().includes(searchTerm));
      })
      .sort((a, b) => priorityScore(b.priority) - priorityScore(a.priority) || a.clientName.localeCompare(b.clientName));
  }, [advisorName, completedItems, itemStatuses, mineOnly, priorityFilter, scopedItems, search, snoozedUntil, sourceFilter, horizonFilter]);

  const itemsByBucket = useMemo(() => {
    const buckets: Record<AdvisorDashboardBucket, AdvisorDashboardItem[]> = { today: [], risk: [], briefing: [], documents: [] };
    for (const item of visibleItems) buckets[item.bucket].push(item);
    return buckets;
  }, [visibleItems]);

  const selectedItem = visibleItems.find((item) => item.id === selectedItemId) || visibleItems[0] || null;
  const selectedNote = selectedItem ? notesByClient[selectedItem.clientName] || '' : '';

  const quickWins = useMemo(() => visibleItems.filter((item) => {
    const text = `${item.title} ${item.nextAction}`.toLowerCase();
    return text.includes('ouvrir') || text.includes('préparer') || text.includes('envoyer') || text.includes('créer');
  }).slice(0, 6), [visibleItems]);

  const dataIssues = useMemo(() => {
    const issues: Array<{ id: string; label: string; detail: string; item?: AdvisorDashboardItem }> = [];
    for (const source of data?.sourceStatus || []) {
      if (!source.loaded) issues.push({ id: `source-${source.source}`, label: `${source.label} indisponible`, detail: source.detail });
    }
    for (const item of visibleItems) {
      if (!item.owner) issues.push({ id: `owner-${item.id}`, label: 'Conseiller/référent manquant', detail: item.clientName, item });
      if (!item.nextAction) issues.push({ id: `action-${item.id}`, label: 'Prochaine action manquante', detail: item.clientName, item });
      if (item.signal.toLowerCase().includes('tâche projet manquante')) issues.push({ id: `task-${item.id}`, label: 'Tâche projet à créer', detail: item.clientName, item });
    }
    return issues.slice(0, 10);
  }, [data, visibleItems]);

  const dayPlan = useMemo(() => {
    const high = visibleItems.filter((item) => item.priority === 'high');
    return {
      highCount: high.length,
      quickWinCount: quickWins.length,
      issueCount: dataIssues.length,
      briefingCount: itemsByBucket.briefing.length,
      nextItems: visibleItems.slice(0, 5),
    };
  }, [dataIssues.length, itemsByBucket.briefing.length, quickWins.length, visibleItems]);

  const dayPlanDraftItems = useMemo(() => dayPlanDraftRows.map((row) => ({
    row,
    item: visibleItems.find((candidate) => candidate.id === row.itemId) || null,
  })).filter((entry): entry is { row: DayPlanDraftRow; item: AdvisorDashboardItem } => Boolean(entry.item)), [dayPlanDraftRows, visibleItems]);
  const scheduledDraftItems = dayPlanDraftItems.filter((entry) => entry.row.decision === 'scheduled');
  const deferredDraftItems = dayPlanDraftItems.filter((entry) => entry.row.decision === 'tomorrow');
  const draftDurationOverrides = Object.fromEntries(scheduledDraftItems.map(({ row }) => [row.itemId, row.durationMinutes]));
  const draftEstimatedMinutes = scheduledDraftItems.reduce((sum, { row }) => sum + row.durationMinutes, 0);
  const draftCalendarBlocks = buildCalendarBlocks(scheduledDraftItems.map(({ item }) => item), draftDurationOverrides);
  const hasAfternoonBlocks = draftCalendarBlocks.some((block) => block.startTime >= AFTERNOON_START);
  const draftSummary = `${scheduledDraftItems.length} bloc(s) au plan · ${deferredDraftItems.length} report(s) · ${formatEffort(draftEstimatedMinutes)} · ${hasAfternoonBlocks ? 'AM + PM' : 'AM'}`;

  const seedDayPlanningDraft = () => {
    const baseItems = visibleItems.slice(0, FULL_DAY_PLAN_LIMIT);
    const blocksByItemId = Object.fromEntries((dailyPlan?.calendarBlocks || []).map((block) => [block.itemId || '', block.durationMinutes]));
    setDayPlanDraftRows(baseItems.map((item) => ({
      itemId: item.id,
      durationMinutes: blocksByItemId[item.id] || estimatedMinutes(item),
      decision: 'scheduled' as DayPlanDraftDecision,
    })));
    setDraftBriefingBody(dailyPlan?.emailDraft?.body || buildDailyEmailDraft(activeAdvisorName || currentAdvisorName, baseItems, dataIssues.length, baseItems.reduce((sum, item) => sum + estimatedMinutes(item), 0)).body);
    setDayPlanningOverlayOpen(true);
  };

  const moveDayPlanDraftRow = (itemId: string, direction: -1 | 1) => {
    setDayPlanDraftRows((rows) => {
      const index = rows.findIndex((row) => row.itemId === itemId);
      const nextIndex = index + direction;
      if (index < 0 || nextIndex < 0 || nextIndex >= rows.length) return rows;
      const next = [...rows];
      [next[index], next[nextIndex]] = [next[nextIndex], next[index]];
      return next;
    });
  };

  const updateDayPlanDraftDuration = (itemId: string, durationMinutes: number) => {
    setDayPlanDraftRows((rows) => rows.map((row) => row.itemId === itemId ? { ...row, durationMinutes } : row));
  };

  const updateDayPlanDraftDecision = (itemId: string, decision: DayPlanDraftDecision) => {
    setDayPlanDraftRows((rows) => rows.map((row) => row.itemId === itemId ? { ...row, decision } : row));
  };

  const saveDayPlanDraft = () => {
    setDailyApprovalState('proposed');
    addHistory('Brouillon de journée enregistré', undefined, draftSummary);
    setDayPlanningOverlayOpen(false);
  };

  const handleAction = (action: AdvisorDashboardItem['actions'][number], item: AdvisorDashboardItem) => {
    addHistory(`Action ouverte : ${action.label}`, item, item.nextAction);
    if (action.href) {
      window.open(action.href, '_blank', 'noopener,noreferrer');
      return;
    }
    if (action.kind === 'open-usecure') {
      onClose();
      onOpenUsecure();
      return;
    }
    if (action.kind === 'open-offers') {
      onClose();
      onOpenOffers();
      return;
    }
    if (action.kind === 'prepare-briefing') {
      openEdithAssistant(advisorContextPrompt('Prépare un briefing conseiller clair pour ce client: points à savoir, message à dire au client, décisions à obtenir et prochaine action.', item), item);
      return;
    }
    if (action.kind === 'prepare-document') {
      openEdithAssistant(advisorContextPrompt('Aide-moi à préparer ou réviser le document/livrable pertinent pour ce client: contenu attendu, informations manquantes et étapes de validation.', item), item);
      return;
    }
    openItemDetails(item.id);
  };

  const prepareMyDay = () => {
    if (!canEditWorkflow) {
      addHistory('Vue lecture seule', undefined, 'Basculez sur Ma journée ou activez le mode coordonnateur pour modifier.');
      return;
    }
    const preparedAt = new Date().toISOString();
    const manualItemsForPlan = scheduledDraftItems.map(({ item }) => item);
    const manualDurationOverrides = Object.fromEntries(scheduledDraftItems.map(({ row }) => [row.itemId, row.durationMinutes]));
    const focusItemsForPlan = manualItemsForPlan.length ? manualItemsForPlan.filter((item) => item.bucket === 'today' || item.priority === 'high').slice(0, 3) : dayPlan.nextItems.slice(0, 3);
    const correctionItemsForPlan = manualItemsForPlan.length
      ? (draftIncludeCorrections ? manualItemsForPlan.filter((item) => dataIssues.some((issue) => issue.item?.id === item.id)) : [])
      : dataIssues.filter((issue) => issue.item).slice(0, 4).map((issue) => issue.item!);
    const validationItemsForPlan = manualItemsForPlan.length
      ? manualItemsForPlan.filter((item) => item.bucket === 'briefing' || item.bucket === 'documents').slice(0, 4)
      : [...itemsByBucket.briefing, ...itemsByBucket.documents].slice(0, 4);
    const scheduledItems = manualItemsForPlan.length ? manualItemsForPlan : [...focusItemsForPlan, ...correctionItemsForPlan, ...validationItemsForPlan]
      .filter((item, index, arr) => arr.findIndex((candidate) => candidate.id === item.id) === index)
      .slice(0, FULL_DAY_PLAN_LIMIT);
    const estimatedTotal = scheduledItems.reduce((sum, item) => sum + (manualDurationOverrides[item.id] || estimatedMinutes(item)), 0);
    const nextStatuses = { ...itemStatuses };
    const nextSnoozedUntil = { ...snoozedUntil };
    deferredDraftItems.forEach(({ item }) => { nextSnoozedUntil[item.id] = tomorrowIso(); });
    focusItemsForPlan.forEach((item) => { nextStatuses[item.id] = nextStatuses[item.id] || 'a_traiter'; });
    correctionItemsForPlan.forEach((item) => { nextStatuses[item.id] = 'a_valider'; });
    validationItemsForPlan.forEach((item) => { nextStatuses[item.id] = nextStatuses[item.id] || 'a_valider'; });
    const automationActions = [
      ...correctionItemsForPlan.map((item) => ({ id: `validate-${item.id}`, itemId: item.id, clientName: item.clientName, label: `Mettre ${item.clientName} à valider pour correction`, status: 'a_valider' as CyberAdvisorItemWorkflowStatus })),
      ...validationItemsForPlan.map((item) => ({ id: `brief-${item.id}`, itemId: item.id, clientName: item.clientName, label: `Préparer/valider ${item.clientName}`, status: nextStatuses[item.id] })),
    ].slice(0, 8);
    const nextPlan: CyberAdvisorDailyPlan = {
      preparedAt,
      headline: `${focusItemsForPlan.length} priorité(s), ${correctionItemsForPlan.length} correction(s), ${validationItemsForPlan.length} validation(s)`,
      focusItemIds: focusItemsForPlan.map((item) => item.id),
      correctionItemIds: correctionItemsForPlan.map((item) => item.id),
      validationItemIds: validationItemsForPlan.map((item) => item.id),
      estimatedMinutes: estimatedTotal,
      calendarBlocks: buildCalendarBlocks(scheduledItems, manualDurationOverrides),
      emailDraft: {
        ...buildDailyEmailDraft(activeAdvisorName || currentAdvisorName, scheduledItems, dataIssues.length, estimatedTotal),
        body: draftBriefingBody || buildDailyEmailDraft(activeAdvisorName || currentAdvisorName, scheduledItems, dataIssues.length, estimatedTotal).body,
      },
      priorityRationale: buildPriorityRationale(scheduledItems),
      automationActions,
      advisorKey: normalizeAdvisor(activeAdvisorName || currentAdvisorName || user?.email || 'global'),
      advisorName: activeAdvisorName || currentAdvisorName || undefined,
    };
    setPreparedPlanAt(preparedAt);
    setDailyPlan(nextPlan);
    setItemStatuses(nextStatuses);
    setSnoozedUntil(nextSnoozedUntil);
    setSelectedItemId(scheduledItems[0]?.id || selectedItemId);
    setQueueMode(true);
    setDailyApprovalState('approved');
    setDayPlanningOverlayOpen(false);
    void persistStatePatch({ dailyPlan: nextPlan, itemStatuses: nextStatuses, snoozedUntil: nextSnoozedUntil });
    addHistory('Journée préparée automatiquement', undefined, `${nextPlan.headline} · ${deferredDraftItems.length} report(s) · horaire interne + brouillon quotidien · ${formatEffort(nextPlan.estimatedMinutes)}`);
  };

  const prepareAllBriefings = () => {
    const count = itemsByBucket.briefing.length;
    const question = advisorContextPrompt(`Prépare les briefings prioritaires en lot pour les ${count} item(s) de briefing visibles. Pour chaque client, donne: à savoir, message client, décision à obtenir, prochaine action.`);
    openEdithAssistant(question, itemsByBucket.briefing[0]);
  };

  const prepareAllRelances = () => {
    const relanceItems = visibleItems.filter((item) => item.nextAction.toLowerCase().includes('relance') || item.title.toLowerCase().includes('relancer'));
    const question = advisorContextPrompt(`Prépare les relances prioritaires en lot pour les ${relanceItems.length} item(s) visibles. Donne un brouillon court par client et les données à valider avant envoi.`, relanceItems[0]);
    openEdithAssistant(question, relanceItems[0]);
  };


  const copyDailyEmailDraft = async () => {
    if (!dailyPlan?.emailDraft) return;
    const content = `${dailyPlan.emailDraft.subject}

${dailyPlan.emailDraft.body}`;
    try {
      await navigator.clipboard.writeText(content);
      addHistory('Brouillon quotidien copié', undefined, dailyPlan.emailDraft.subject);
    } catch {
      addHistory('Brouillon quotidien prêt', undefined, dailyPlan.emailDraft.subject);
    }
  };

  const exportDailyCalendar = () => {
    if (!dailyPlan?.calendarBlocks?.length) {
      addHistory('Calendrier non prêt', undefined, 'Planifiez et approuvez la journée avant de télécharger le fichier calendrier.');
      seedDayPlanningDraft();
      return;
    }
    const blob = new Blob([buildIcs(dailyPlan)], { type: 'text/calendar;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = `bureau-conseiller-${new Date().toISOString().slice(0, 10)}.ics`;
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
    URL.revokeObjectURL(url);
    addHistory('Calendrier de journée exporté', undefined, `${dailyPlan.calendarBlocks.length} bloc(s)`);
  };

  const markDone = (item: AdvisorDashboardItem) => {
    if (!canEditWorkflow) {
      addHistory('Vue lecture seule', undefined, 'Basculez sur Ma journée ou activez le mode coordonnateur pour modifier.');
      return;
    }
    const next = { ...completedItems, [item.id]: new Date().toISOString() };
    const nextStatuses = { ...itemStatuses, [item.id]: 'traite' as CyberAdvisorItemWorkflowStatus };
    setCompletedItems(next);
    setItemStatuses(nextStatuses);
    void persistStatePatch({ completedItems: next, itemStatuses: nextStatuses });
    addHistory('Item marqué traité', item, item.nextAction);
    const nextItem = visibleItems.find((candidate) => candidate.id !== item.id);
    setSelectedItemId(nextItem?.id || null);
  };

  const snooze = (item: AdvisorDashboardItem, until: string) => {
    if (!canEditWorkflow) {
      addHistory('Vue lecture seule', undefined, 'Basculez sur Ma journée ou activez le mode coordonnateur pour modifier.');
      return;
    }
    const next = { ...snoozedUntil, [item.id]: until };
    setSnoozedUntil(next);
    void persistStatePatch({ snoozedUntil: next });
    addHistory(`Item reporté au ${until}`, item, item.signal);
    const nextItem = visibleItems.find((candidate) => candidate.id !== item.id);
    setSelectedItemId(nextItem?.id || null);
  };

  const updateItemStatus = (item: AdvisorDashboardItem, status: CyberAdvisorItemWorkflowStatus) => {
    if (!canEditWorkflow) {
      addHistory('Vue lecture seule', undefined, 'Basculez sur Ma journée ou activez le mode coordonnateur pour modifier.');
      return;
    }
    const nextStatuses = { ...itemStatuses, [item.id]: status };
    setItemStatuses(nextStatuses);
    void persistStatePatch({ itemStatuses: nextStatuses });
    addHistory(`Statut conseiller : ${workflowStatusMeta(status).label}`, item, item.nextAction);
    if (status === 'traite') {
      const nextCompleted = { ...completedItems, [item.id]: new Date().toISOString() };
      setCompletedItems(nextCompleted);
      void persistStatePatch({ completedItems: nextCompleted, itemStatuses: nextStatuses });
    }
  };

  const setItemStatus = (item: AdvisorDashboardItem, status: CyberAdvisorItemWorkflowStatus) => updateItemStatus(item, status);

  const handleCorrectionAction = async (issue: { id: string; label: string; detail: string; item?: AdvisorDashboardItem }) => {
    if (!canEditWorkflow) {
      addHistory('Vue lecture seule', issue.item, 'Correction non appliquée.');
      return;
    }
    if (!issue.item) {
      addHistory(`Correction à vérifier : ${issue.label}`, undefined, issue.detail);
      return;
    }
    const item = issue.item;
    openItemDetails(item.id);
    if (item.correctionKind === 'create_project_task' || issue.label.toLowerCase().includes('tâche')) {
      updateItemStatus(item, 'a_valider');
      if (item.sourceRecordType === 'usecure_followup' && typeof item.sourceRecordId === 'number') {
        try {
          const result = await createUsecureFollowupProjectTask(item.sourceRecordId);
          const taskUrl = result.projectTaskLink?.url;
          addHistory(result.created ? 'Tâche projet créée' : 'Tâche projet déjà existante', item, taskUrl || result.projectTaskLink?.title);
          updateItemStatus(item, 'donnee_corrigee');
          if (taskUrl) window.open(taskUrl, '_blank', 'noopener,noreferrer');
          void loadDashboard();
          return;
        } catch (err) {
          addHistory('Création tâche projet à faire manuellement', item, err instanceof Error ? err.message : 'Erreur API');
          if (!openSourceUrl(item)) openItemDetails(item.id);
          return;
        }
      }
      const projectAction = item.actions.find((action) => action.kind === 'open-project' || action.href?.includes('/projets-v2'));
      if (projectAction) handleAction(projectAction, item);
      else if (!openSourceUrl(item)) addHistory('Correction demandée : créer tâche projet', item, 'Aucun lien projet direct trouvé');
      return;
    }
    if (item.correctionKind === 'assign_referent' || issue.label.toLowerCase().includes('référent') || issue.label.toLowerCase().includes('conseiller')) {
      updateItemStatus(item, 'a_valider');
      addHistory('Correction demandée : assigner référent', item, 'Source métier ouverte pour validation');
      openSourceUrl(item);
      return;
    }
    if (item.correctionKind === 'review_document') {
      updateItemStatus(item, 'a_valider');
      openEdithAssistant(advisorContextPrompt('Prépare la liste des validations documentaires à faire avant de marquer ce dossier comme corrigé.', item), item);
      return;
    }
    updateItemStatus(item, 'donnee_corrigee');
    addHistory('Correction marquée à valider', item, issue.label);
    openSourceUrl(item);
  };

  const nextQueueItem = () => {
    if (!selectedItem) {
      setSelectedItemId(visibleItems[0]?.id || null);
      return;
    }
    const currentIndex = visibleItems.findIndex((item) => item.id === selectedItem.id);
    const next = visibleItems[currentIndex + 1] || visibleItems[0];
    setSelectedItemId(next?.id || null);
  };

  const updateNote = (value: string) => {
    if (!canEditWorkflow) return;
    if (!selectedItem) return;
    const next = { ...notesByClient, [selectedItem.clientName]: value };
    setNotesByClient(next);
    void persistStatePatch({ notesByClient: next });
  };

  const focusItems = visibleItems.slice(0, 2);
  const primaryFocus = focusItems[0] || selectedItem;
  const secondaryFocus = focusItems[1] || visibleItems.find((item) => item.id !== primaryFocus?.id) || null;
  const totalEstimatedMinutes = visibleItems.slice(0, 10).reduce((sum, item) => sum + estimatedMinutes(item), 0);
  const lateCount = visibleItems.filter((item) => item.priority === 'high').length;
  const dueThisWeekCount = visibleItems.filter((item) => item.priority === 'medium').length;
  const almostDoneCount = itemsByBucket.documents.length;
  const mandateRows = visibleItems.slice(0, 5).map((item, index) => ({ item, progress: mandateProgress(item, index) }));
  const attentionCards = [
    { label: 'Livrables en retard', value: lateCount, detail: 'Nécessitent votre attention', icon: AlertTriangle, tone: 'text-red-700 bg-red-50 border-red-100' },
    { label: 'Clients bloqués', value: dataIssues.length, detail: 'En attente d’une action', icon: Clock, tone: 'text-amber-700 bg-amber-50 border-amber-100' },
    { label: 'Comités à préparer', value: itemsByBucket.briefing.length, detail: 'Briefings ou rencontres', icon: CalendarCheck, tone: 'text-indigo-700 bg-indigo-50 border-indigo-100' },
  ];
  const opportunityCards = quickWins.slice(0, 3).map((item, index) => ({
    item,
    relevance: Math.max(7, 9 - index),
    potential: [4500, 3200, 5000][index] || 2500,
  }));
  const meetingCards = (itemsByBucket.briefing.length ? itemsByBucket.briefing : visibleItems).slice(0, 3).map((item, index) => ({
    item,
    day: index === 0 ? 'Demain' : index === 1 ? 'Dans 2 jours' : 'Cette semaine',
    time: ['10:00', '14:00', '09:30'][index] || '09:00',
  }));
  const deliverableCards = (itemsByBucket.documents.length ? itemsByBucket.documents : visibleItems).slice(0, 4).map((item, index) => ({
    item,
    progress: Math.min(95, 40 + index * 15 + (item.priority === 'low' ? 10 : 0)),
  }));
  const activityCards = historyEvents.slice(0, 4).map((event) => ({
    id: event.id,
    title: event.label,
    clientName: event.clientName || 'Bureau conseiller',
    detail: event.detail || new Date(event.at).toLocaleString('fr-CA'),
  }));

  const horizonCards = [
    { label: 'Aujourd’hui', count: visibleItems.filter((item) => item.priority === 'high').length || visibleItems.slice(0, 2).length, minutes: visibleItems.slice(0, 4).reduce((sum, item) => sum + estimatedMinutes(item), 0), detail: 'Actions qui débloquent la journée', tone: 'border-red-100 bg-red-50 text-red-800' },
    { label: '7 jours', count: visibleItems.filter((item) => item.priority !== 'low').length, minutes: visibleItems.slice(0, 8).reduce((sum, item) => sum + estimatedMinutes(item), 0), detail: 'Échéances, comités et relances', tone: 'border-amber-100 bg-amber-50 text-amber-800' },
    { label: '30 jours', count: visibleItems.length, minutes: totalEstimatedMinutes, detail: 'Livraison active et documents', tone: 'border-blue-100 bg-blue-50 text-blue-800' },
    { label: '90 jours', count: new Set(visibleItems.map((item) => item.clientName)).size, minutes: Math.round(totalEstimatedMinutes * 1.6), detail: 'Renouvellements, risques et opportunités', tone: 'border-indigo-100 bg-indigo-50 text-indigo-800' },
  ];
  const impactMatrix = [
    { label: 'Faire maintenant', detail: 'Urgent + impact élevé', items: visibleItems.filter((item) => item.priority === 'high').slice(0, 3), tone: 'border-red-100 bg-red-50' },
    { label: 'Planifier maintenant', detail: 'Important, à verrouiller au calendrier', items: visibleItems.filter((item) => item.priority === 'medium').slice(0, 3), tone: 'border-blue-100 bg-blue-50' },
    { label: 'Déléguer / préparer', detail: 'Préparation, documents, relances', items: [...itemsByBucket.documents, ...itemsByBucket.briefing].slice(0, 3), tone: 'border-emerald-100 bg-emerald-50' },
    { label: 'Surveiller', detail: 'Long terme ou faible impact immédiat', items: visibleItems.filter((item) => item.priority === 'low').slice(0, 3), tone: 'border-slate-200 bg-slate-50' },
  ];
  const projectPilotageCards = visibleItems
    .filter((item) => item.source === 'projects' || item.actions.some((action) => action.kind === 'open-project' || action.href?.includes('/projets-v2')))
    .slice(0, 4);
  const usecureOperationalCards = visibleItems.filter((item) => item.source === 'usecure').slice(0, 4);
  const blockerCards = [
    ...dataIssues.map((issue) => ({ id: issue.id, label: issue.label, detail: issue.detail, item: issue.item })),
    ...visibleItems.filter((item) => item.signal.toLowerCase().includes('manquante') || item.why.some((reason) => reason.toLowerCase().includes('attente') || reason.toLowerCase().includes('bloqu'))).map((item) => ({ id: `blocker-${item.id}`, label: 'Dépendance ou bloquant', detail: `${item.clientName} · ${item.signal}`, item })),
  ].slice(0, 6);
  const dayRoadmap = [
    { label: 'Maintenant', action: primaryFocus?.nextAction || 'Choisir la première priorité', clientName: primaryFocus?.clientName || 'Bureau conseiller', effort: primaryFocus ? formatEffort(estimatedMinutes(primaryFocus)) : '—' },
    { label: 'Après-midi', action: secondaryFocus?.nextAction || 'Préparer les briefings à venir', clientName: secondaryFocus?.clientName || 'Briefings', effort: secondaryFocus ? formatEffort(estimatedMinutes(secondaryFocus)) : '30 min' },
    { label: 'Fin de journée', action: blockerCards.length ? 'Débloquer les dépendances restantes' : 'Confirmer les actions traitées', clientName: blockerCards[0]?.detail || 'File conseiller', effort: blockerCards.length ? '20 min' : '10 min' },
    { label: 'Demain matin', action: horizonCards[1].count ? 'Replanifier le 7 jours' : 'Réviser les suivis long terme', clientName: 'Horizon 7 jours', effort: '15 min' },
  ];

  const todayActionItems = visibleItems.slice(0, 8);
  const blockedActionItems = blockerCards.filter((blocker) => blocker.item).slice(0, 6);
  const waitingItems = visibleItems.filter((item) => item.signal.toLowerCase().includes('attente') || item.why.some((reason) => reason.toLowerCase().includes('attente'))).slice(0, 4);
  const validationItems = [...itemsByBucket.briefing, ...itemsByBucket.documents].slice(0, 4);
  const doneTodayCount = Object.values(completedItems).filter((completedAt) => completedAt.slice(0, 10) === new Date().toISOString().slice(0, 10)).length;
  const primaryAction = selectedItem || primaryFocus || todayActionItems[0] || null;
  const selectedReasons = selectedItem ? (selectedItem.why.length ? selectedItem.why : [selectedItem.signal]).slice(0, 4) : [];
  const selectedHistory = selectedItem
    ? historyEvents.filter((event) => event.clientName === selectedItem.clientName).slice(0, 3)
    : [];
  const selectedBlockers = selectedItem
    ? dataIssues.filter((issue) => issue.item?.id === selectedItem.id || issue.detail === selectedItem.clientName).slice(0, 3)
    : [];
  const selectedActions = selectedItem?.actions || [];
  const sourceHealth = data?.sourceStatus || [];
  const followedItems = scopedItems.filter((item) => Boolean(itemStatuses[item.id]) || Boolean(snoozedUntil[item.id]) || Boolean(completedItems[item.id])).slice(0, 24) || [];
  const signalDiagnostics = [
    { label: 'Signaux actifs', value: data?.items.length || 0 },
    { label: advisorScope === 'all' ? 'Vue globale' : 'Vue conseiller', value: scopedItems.length },
    { label: 'Après filtres', value: visibleItems.length },
    { label: 'Sources ouvertes', value: sourceHealth.filter((source) => source.loaded).length },
    { label: 'Doublons réduits', value: Math.max(0, (data?.items.length || 0) - new Set((data?.items || []).map((item) => `${item.clientName}:${item.source}:${item.bucket}:${item.signal}:${item.nextAction}`)).size) },
  ];
  const planFocusItems = dailyPlan ? dailyPlan.focusItemIds.map((id) => visibleItems.find((item) => item.id === id)).filter(Boolean) as AdvisorDashboardItem[] : [];
  const planCorrectionItems = dailyPlan ? dailyPlan.correctionItemIds.map((id) => visibleItems.find((item) => item.id === id)).filter(Boolean) as AdvisorDashboardItem[] : [];
  const planValidationItems = dailyPlan ? dailyPlan.validationItemIds.map((id) => visibleItems.find((item) => item.id === id)).filter(Boolean) as AdvisorDashboardItem[] : [];

  const renderWorkItem = (item: AdvisorDashboardItem, index: number, label?: string) => {
    const firstAction = item.actions[0];
    const status = workflowStatusMeta(itemStatuses[item.id]);
    return (
      <article key={`${label || 'work'}-${item.id}`} className={`rounded-2xl border bg-white p-4 shadow-sm transition hover:border-blue-200 ${selectedItem?.id === item.id ? 'border-blue-300 ring-2 ring-blue-100' : 'border-slate-200'}`}>
        <div className="flex items-start gap-3">
          <button type="button" onClick={() => openItemDetails(item.id)} className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-xl text-xs font-black ${item.priority === 'high' ? 'bg-red-50 text-red-700' : item.priority === 'medium' ? 'bg-amber-50 text-amber-700' : 'bg-blue-50 text-blue-700'}`}>
            {index + 1}
          </button>
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2">
              <span className={`rounded-full border px-2 py-0.5 text-[10px] font-black uppercase tracking-wide ${priorityClasses(item.priority)}`}>{priorityLabel(item.priority)}</span>
              <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-bold text-slate-600">{sourceLabel(item.source)}</span>
              {label && <span className="rounded-full bg-blue-50 px-2 py-0.5 text-[10px] font-bold text-blue-700">{label}</span>}
              <span className={`rounded-full border px-2 py-0.5 text-[10px] font-bold ${status.className}`}>{status.label}</span>
              {isStillSnoozed(item.id, snoozedUntil) && <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-bold text-slate-500">Reporté</span>}
              <span className="rounded-full bg-indigo-50 px-2 py-0.5 text-[10px] font-bold text-indigo-700">{inclusionReason(item)}</span>
            </div>
            <button type="button" onClick={() => openItemDetails(item.id)} className="mt-2 block text-left">
              <p className="text-sm font-black text-slate-950">{item.nextAction || item.title}</p>
              <p className="mt-1 text-xs font-semibold text-slate-600">{item.clientName} · {item.signal}</p>
            </button>
            <div className="mt-3 flex flex-wrap gap-2">
              {firstAction && <button type="button" onClick={() => handleAction(firstAction, item)} className="inline-flex items-center gap-1.5 rounded-xl bg-blue-600 px-3 py-1.5 text-xs font-black text-white hover:bg-blue-700">{firstAction.label}<ArrowRight className="h-3.5 w-3.5" /></button>}
              {(item.sourceUrl || item.actions.some((action) => action.href)) && <button type="button" onClick={() => openSourceUrl(item)} className="inline-flex items-center gap-1.5 rounded-xl border border-slate-200 bg-white px-3 py-1.5 text-xs font-black text-slate-700 hover:border-blue-300"><ExternalLink className="h-3.5 w-3.5" />Source</button>}
              <button type="button" onClick={() => openEdithAssistant(advisorContextPrompt('Explique pourquoi ce dossier est prioritaire et propose la prochaine action la plus utile.', item), item)} className="inline-flex items-center gap-1.5 rounded-xl border border-blue-100 bg-blue-50 px-3 py-1.5 text-xs font-black text-blue-700 hover:bg-blue-100"><Sparkles className="h-3.5 w-3.5" />Demander à Edith</button>
              <button type="button" disabled={!canEditWorkflow} onClick={() => snooze(item, tomorrowIso())} className="rounded-xl border border-slate-200 px-3 py-1.5 text-xs font-bold text-slate-600 hover:border-slate-300 disabled:cursor-not-allowed disabled:opacity-50">Reporter demain</button>
              <select disabled={!canEditWorkflow} value={itemStatuses[item.id] || 'a_traiter'} onChange={(event) => updateItemStatus(item, event.target.value as CyberAdvisorItemWorkflowStatus)} className="rounded-xl border border-slate-200 bg-white px-2 py-1.5 text-xs font-bold text-slate-700 disabled:cursor-not-allowed disabled:opacity-50">{workflowStatuses.map((candidate) => <option key={candidate.value} value={candidate.value}>{candidate.label}</option>)}</select>
            </div>
          </div>
          <div className="shrink-0 text-right text-xs text-slate-500">
            <Clock className="ml-auto h-4 w-4" />
            <strong className="mt-1 block text-slate-800">{formatEffort(estimatedMinutes(item))}</strong>
          </div>
        </div>
      </article>
    );
  };

  const renderMiniItem = (item: AdvisorDashboardItem, label: string) => (
    <button key={`${label}-${item.id}`} type="button" onClick={() => openItemDetails(item.id)} className="w-full rounded-xl border border-slate-100 bg-slate-50 p-3 text-left hover:border-blue-200 hover:bg-blue-50">
      <div className="flex items-center justify-between gap-3">
        <p className="truncate text-sm font-bold text-slate-800">{item.clientName}</p>
        <span className={`shrink-0 rounded-full px-2 py-0.5 text-[10px] font-bold ${item.priority === 'high' ? 'bg-red-50 text-red-700' : item.priority === 'medium' ? 'bg-amber-50 text-amber-700' : 'bg-slate-100 text-slate-600'}`}>{label}</span>
      </div>
      <p className="mt-1 line-clamp-2 text-xs text-slate-500">{item.nextAction || item.signal}</p>
    </button>
  );

  const renderDataIssue = (issue: { id: string; label: string; detail: string; item?: AdvisorDashboardItem }) => (
    <div key={issue.id} className="rounded-xl border border-amber-100 bg-amber-50 p-3">
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="text-sm font-black text-amber-950">{issue.label}</p>
          <p className="mt-1 text-xs font-semibold text-amber-800">{issue.detail}</p>
        </div>
        <AlertTriangle className="h-4 w-4 shrink-0 text-amber-600" />
      </div>
      <div className="mt-3 flex flex-wrap gap-2">
        {issue.item && <button type="button" onClick={() => openItemDetails(issue.item!.id)} className="rounded-xl bg-white px-3 py-1.5 text-xs font-black text-amber-800 shadow-sm">Voir dossier</button>}
        <button type="button" onClick={() => handleCorrectionAction(issue)} className="rounded-xl bg-amber-600 px-3 py-1.5 text-xs font-black text-white">Corriger / créer l’action</button>
        <button type="button" onClick={() => openEdithAssistant(advisorContextPrompt(`Aide-moi à corriger ce blocage: ${issue.label} — ${issue.detail}.`, issue.item), issue.item)} className="rounded-xl border border-amber-200 bg-white px-3 py-1.5 text-xs font-black text-amber-800">Demander à Edith</button>
      </div>
    </div>
  );

  if (!isOpen) return null;

  const renderActionButton = (item: AdvisorDashboardItem, primary = false) => {
    const firstAction = item.actions[0];
    const status = workflowStatusMeta(itemStatuses[item.id]);
    return (
      <button
        type="button"
        onClick={() => firstAction ? handleAction(firstAction, item) : openItemDetails(item.id)}
        className={`inline-flex items-center justify-center gap-2 rounded-xl px-3 py-2 text-xs font-bold transition ${primary ? actionTone(item) : 'border border-slate-200 bg-white text-blue-700 hover:border-blue-300'}`}
      >
        {firstAction?.label || 'Ouvrir'}
        <ArrowRight className="h-3.5 w-3.5" />
      </button>
    );
  };

  const renderFocusCard = (item: AdvisorDashboardItem, index: number) => {
    const isPrimary = index === 0;
    const tone = item.priority === 'high' ? 'border-l-red-500' : item.priority === 'medium' ? 'border-l-amber-400' : 'border-l-emerald-500';
    return (
      <article key={item.id} className={`rounded-2xl border border-slate-200 border-l-4 ${tone} bg-white p-5 shadow-sm`}>
        <div className="flex items-start justify-between gap-4">
          <div className="flex min-w-0 gap-4">
            <div className={`flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl text-lg font-black ${item.priority === 'high' ? 'bg-red-50 text-red-700' : item.priority === 'medium' ? 'bg-amber-50 text-amber-700' : 'bg-blue-50 text-blue-700'}`}>
              {clientInitials(item.clientName)}
            </div>
            <div className="min-w-0">
              <span className={`mb-2 inline-flex rounded-full px-2.5 py-1 text-[11px] font-black uppercase tracking-wide ${isPrimary ? 'bg-red-50 text-red-700' : 'bg-amber-50 text-amber-700'}`}>
                {isPrimary ? 'Priorité #1' : 'Ensuite'}
              </span>
              <p className="text-sm font-semibold text-slate-600">{item.clientName}</p>
              <h3 className="mt-1 text-xl font-black text-slate-950">{item.nextAction}</h3>
              <div className="mt-3 flex flex-wrap gap-2">
                <span className="rounded-full bg-slate-100 px-2.5 py-1 text-xs font-bold text-slate-700">{item.title}</span>
                <span className={`rounded-full border px-2.5 py-1 text-xs font-bold ${priorityClasses(item.priority)}`}>{priorityLabel(item.priority)}</span>
              </div>
            </div>
          </div>
          <div className="shrink-0 text-right">
            <div className="flex items-center justify-end gap-1 text-xl font-black text-slate-950"><Clock className="h-5 w-5" />{formatEffort(estimatedMinutes(item))}</div>
            <p className="text-xs text-slate-500">Temps estimé</p>
          </div>
        </div>
        <div className="mt-4 grid gap-4 lg:grid-cols-[1fr_auto] lg:items-end">
          <div>
            <p className="text-xs font-black uppercase tracking-wide text-slate-500">Pourquoi c’est prioritaire</p>
            <ul className="mt-2 space-y-1.5 text-sm text-slate-700">
              {(item.why.length ? item.why : [item.signal]).slice(0, 3).map((reason) => (
                <li key={reason} className="flex items-center gap-2"><CheckCircle className="h-4 w-4 text-red-500" />{reason}</li>
              ))}
            </ul>
          </div>
          {renderActionButton(item, true)}
        </div>
        <div className="mt-4 flex flex-wrap gap-2">
          <button type="button" onClick={() => openEdithAssistant(advisorContextPrompt('Prépare un courriel court et prêt à copier pour cette action client.', item), item)} className="inline-flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs font-bold text-slate-700"><Mail className="h-3.5 w-3.5" />Préparer courriel</button>
          <button type="button" onClick={() => openItemDetails(item.id)} className="inline-flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs font-bold text-slate-700"><StickyNote className="h-3.5 w-3.5" />Ouvrir les notes du client</button>
          <button type="button" onClick={() => snooze(item, tomorrowIso())} className="inline-flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs font-bold text-slate-700"><MoreHorizontal className="h-3.5 w-3.5" />Reporter</button>
        </div>
      </article>
    );
  };

  const contextItem = contextDrawerItem || selectedItem || primaryAction;
  const nextDayItems = visibleItems.filter((item) => item.id !== primaryAction?.id).slice(0, 3);
  const weeklyCount = visibleItems.filter((item) => item.priority !== 'low').length;
  const watchCount = visibleItems.filter((item) => item.priority === 'low' || item.source === 'offers').length;
  const sourceOkCount = sourceHealth.filter((source) => source.loaded).length;
  const horizonOptions: Array<{ key: HorizonFilter; label: string; count: number; tab?: 'attentes' | 'inbox' | 'sources'; view?: AdvisorViewMode }> = [
    { key: 'today', label: 'Aujourd’hui', count: scopedItems.filter((item) => item.priority === 'high' || item.bucket === 'today').length, view: 'actions' },
    { key: 'week', label: 'Cette semaine', count: scopedItems.filter((item) => item.priority !== 'low' || item.bucket === 'briefing' || item.bucket === 'documents').length, view: 'suivis' },
    { key: 'watch', label: 'À surveiller', count: scopedItems.filter((item) => item.priority === 'low' || item.source === 'offers').length, view: 'suivis' },
    { key: 'waiting', label: 'En attente', count: scopedItems.filter((item) => `${item.title} ${item.signal} ${item.nextAction}`.toLowerCase().includes('attente') || `${item.title} ${item.signal} ${item.nextAction}`.toLowerCase().includes('relance')).length, tab: 'attentes' },
    { key: 'inbox', label: 'Inbox', count: scopedItems.filter((item) => !item.owner || !item.nextAction || `${item.title} ${item.signal} ${item.nextAction}`.toLowerCase().includes('manquante') || `${item.title} ${item.signal} ${item.nextAction}`.toLowerCase().includes('bloqu')).length, tab: 'inbox' },
  ];
  const approvalLabel = dailyApprovalState === 'approved' && dailyPlan ? `Journée approuvée · ${new Date(dailyPlan.preparedAt).toLocaleTimeString('fr-CA', { hour: '2-digit', minute: '2-digit' })}` : 'Journée proposée · à approuver';
  const decisionHistory = contextItem
    ? historyEvents.filter((event) => event.clientName === contextItem.clientName).slice(0, 3)
    : [];
  const fallbackDecisions = contextItem ? [
    { id: 'decision-relance', label: 'Relance courte demandée', detail: 'Le client préfère un résumé actionnable.', at: new Date().toISOString() },
    { id: 'decision-suivi', label: 'Suivi source validé', detail: `${sourceLabel(contextItem.source)} prêt pour synthèse.`, at: new Date().toISOString() },
    { id: 'decision-action', label: 'Prochaine action détectée', detail: contextItem.nextAction, at: new Date().toISOString() },
  ] : [];
  const shownDecisions = decisionHistory.length ? decisionHistory : fallbackDecisions;

  const markNotToday = (item: AdvisorDashboardItem) => {
    setItemStatus(item, 'pas_aujourdhui');
    snooze(item, tomorrowIso());
  };

  return (
    <div className="fixed inset-0 z-50 bg-slate-950/60 p-3 backdrop-blur-sm" role="dialog" aria-modal="true" aria-label="Bureau du conseiller">
      <div data-cyber-advisor-dashboard className="mx-auto flex max-h-[96vh] w-full max-w-[1560px] flex-col overflow-hidden rounded-3xl border border-slate-200 bg-slate-50 shadow-2xl">
        <header className="flex flex-col gap-3 border-b border-slate-200 bg-white px-5 py-4 xl:flex-row xl:items-center xl:justify-between">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-blue-600 text-white"><Shield className="h-5 w-5" /></div>
            <div>
              <h2 className="text-xl font-black text-slate-950">Bureau du conseiller</h2>
              <p className="text-xs text-slate-500">Poste de travail guidé : focus principal, contexte à la demande, gestion quotidienne repliée.</p>
            </div>
          </div>
          <div className="flex min-w-0 flex-1 items-center gap-2 xl:max-w-xl">
            <div className="relative min-w-0 flex-1"><Search className="pointer-events-none absolute left-3 top-2.5 h-4 w-4 text-slate-400" /><input value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Rechercher client, document, tâche..." className="w-full rounded-xl border border-slate-200 bg-slate-50 py-2 pl-9 pr-3 text-sm text-slate-800 outline-none focus:border-blue-300 focus:bg-white focus:ring-2 focus:ring-blue-100" /></div>
            <button type="button" disabled={!canEditWorkflow} onClick={seedDayPlanningDraft} className="inline-flex items-center gap-2 rounded-xl bg-blue-600 px-3 py-2 text-sm font-bold text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50"><Sparkles className="h-4 w-4" />Planifier la journée complète</button>
          </div>
          <div className="flex items-center gap-2">
            <button type="button" onClick={() => void loadDashboard()} disabled={loading} className="inline-flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm font-semibold text-slate-700 hover:border-blue-300">{loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}Actualiser</button>
            <button type="button" onClick={onClose} className="rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm font-semibold text-slate-700 hover:border-blue-300">Fermer</button>
          </div>
        </header>

        <main className="overflow-y-auto bg-slate-50 p-5">
          <section aria-label="Horizons de gestion" className="mb-3 flex flex-wrap gap-2 rounded-2xl border border-slate-200 bg-white/80 p-2 shadow-sm">
            {horizonOptions.map((option) => <button key={option.key} type="button" onClick={() => { setHorizonFilter(option.key); if (option.tab) setManagementTab(option.tab); setViewMode(option.view || (option.tab ? 'suivis' : 'actions')); }} className={`rounded-xl px-3 py-2 text-xs font-black ${horizonFilter === option.key ? 'bg-slate-950 text-white' : 'border border-slate-200 bg-white text-slate-700 hover:border-blue-300'}`}>{option.label} <span className="ml-1 opacity-75">{option.count}</span></button>)}
          </section>

          <section aria-label="Rituel de journée" className="mb-3 rounded-2xl border border-blue-100 bg-blue-50 p-3">
            <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
              <div><p className="text-sm font-black text-blue-950">Revue du matin prête</p><p className="text-xs font-semibold text-blue-800">Valider la journée complète — AM + PM — les urgences et les blocages avant de commencer.</p></div>
              <button type="button" onClick={seedDayPlanningDraft} className="rounded-xl bg-blue-600 px-3 py-2 text-xs font-black text-white">Démarrer</button>
            </div>
          </section>

          <section className="mb-4 rounded-3xl border border-slate-200 bg-white p-3 shadow-sm">
            <div className="flex flex-col gap-3 xl:flex-row xl:items-center xl:justify-between">
              <div className="flex flex-wrap gap-2">
                <button type="button" onClick={() => { setAdvisorScope('me'); setSelectedAdvisor(currentAdvisorName); setViewMode('actions'); }} className={`rounded-full px-3 py-1.5 text-xs font-semibold ${advisorScope === 'me' ? 'bg-slate-900 text-white' : 'border border-slate-200 bg-white text-slate-700'}`}>Ma journée</button>
                <button type="button" onClick={() => { setAdvisorScope('all'); setViewMode('suivis'); }} className={`rounded-full px-3 py-1.5 text-xs font-semibold ${advisorScope === 'all' ? 'bg-slate-900 text-white' : 'border border-slate-200 bg-white text-slate-700'}`}>Tous</button>
                <button type="button" onClick={() => { setAdvisorScope('all'); setViewMode('assigner'); setHorizonFilter('inbox'); }} className={`rounded-full px-3 py-1.5 text-xs font-semibold ${viewMode === 'assigner' ? 'bg-amber-600 text-white' : 'border border-slate-200 bg-white text-slate-700'}`}>À assigner</button>
                <label className="flex items-center gap-2 rounded-full border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700">
                  <span>Conseiller sélectionné</span>
                  <select
                    value={selectedAdvisor}
                    onChange={(event) => { setSelectedAdvisor(event.target.value); setAdvisorScope('advisor'); setViewMode('actions'); }}
                    className="bg-transparent text-xs font-black text-slate-900 outline-none"
                    aria-label="Voir la journée de conseiller sélectionné"
                  >
                    <option value="">Voir la journée de...</option>
                    {advisorOptions.map((advisor) => <option key={advisor} value={advisor}>{advisor}</option>)}
                  </select>
                </label>
                <button type="button" onClick={() => setFiltersCollapsed(!filtersCollapsed)} className="rounded-full border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700">Filtres</button>
              </div>
              <div className="flex flex-wrap gap-2 text-xs font-black">
                <span className="rounded-full bg-blue-50 px-3 py-1.5 text-blue-800">{activeAdvisorName || 'Vue globale'} · {canEditWorkflow ? (coordinatorEdit ? 'Modification coordonnateur active' : 'modifiable') : 'Lecture seule'}</span>
                {isViewingOtherAdvisor && <span className="rounded-full bg-purple-50 px-3 py-1.5 text-purple-800">Voir la journée de {activeAdvisorName}</span>}
                {(isViewingOtherAdvisor || advisorScope === 'all') && !isPrismeAdmin && <button type="button" onClick={() => setCoordinatorEdit(!coordinatorEdit)} className="rounded-full border border-purple-200 bg-white px-3 py-1.5 text-purple-800">{coordinatorEdit ? 'Modification coordonnateur active' : 'Lecture seule'}</button>}
                <span className="rounded-full bg-emerald-50 px-3 py-1.5 text-emerald-800">{sourceOkCount}/{sourceHealth.length || 0} sources OK</span>
                <span className="rounded-full bg-amber-50 px-3 py-1.5 text-amber-800">{dataIssues.length} blocages</span>
                <span className="rounded-full bg-slate-100 px-3 py-1.5 text-slate-700">{formatEffort(totalEstimatedMinutes)} prévue</span>
                <span className={`rounded-full px-3 py-1.5 ${dailyApprovalState === 'approved' ? 'bg-emerald-50 text-emerald-800' : 'bg-amber-50 text-amber-800'}`}>{approvalLabel}</span>
              </div>
            </div>
            {!filtersCollapsed && <div className="mt-3 grid gap-2 border-t border-slate-100 pt-3 md:grid-cols-4">
              {(['all', 'usecure', 'offers', 'projects', 'documents'] as Array<SourceFilter>).map((source) => <button key={source} type="button" onClick={() => setSourceFilter(source)} className={`rounded-xl px-3 py-2 text-xs font-black ${sourceFilter === source ? 'bg-blue-600 text-white' : 'border border-slate-200 bg-white text-slate-700'}`}>{source === 'all' ? 'Toutes sources' : sourceLabel(source)}</button>)}
            </div>}
          </section>

          {error && <div className="mb-4 rounded-2xl border border-red-100 bg-red-50 p-4 text-sm font-semibold text-red-700">{error}</div>}

          <section className="grid gap-5 2xl:grid-cols-[minmax(0,1.25fr)_380px]">
            <div className="space-y-5">
              <article ref={selectedPanelRef} className="overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-sm">
                <div className="bg-gradient-to-br from-blue-950 via-blue-900 to-blue-700 p-5 text-white">
                  <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                    <div>
                      <p className="text-xs font-black uppercase tracking-[0.2em] text-blue-200">Focus actuel · Action actuelle · Priorité #1</p>
                      <span className="mt-3 inline-flex rounded-full border border-white/20 bg-white/10 px-3 py-1 text-xs font-black">Prochaine action</span>
                      <h3 className="mt-3 text-3xl font-black leading-tight">{primaryAction?.nextAction || 'Choisir la première priorité'}</h3>
                      <p className="mt-2 text-sm text-blue-100">Focus actuel du conseiller : l’action à traiter maintenant, choisie selon priorité, échéance et signaux actifs.</p>
                      <p className="mt-2 text-sm text-blue-100">{primaryAction ? `${primaryAction.clientName} — ${primaryAction.signal}` : 'La file est prête dès qu’un dossier actif existe.'}</p>
                      {primaryAction && <button type="button" onClick={() => { setSelectedItemId(primaryAction.id); setContextDrawerItem(primaryAction); }} className="mt-3 rounded-full border border-white/20 bg-white/10 px-3 py-1.5 text-xs font-black text-white hover:bg-white/15">Contexte client</button>}
                    </div>
                    <div className="grid min-w-[240px] grid-cols-3 gap-2 text-center">
                      <div className="rounded-2xl bg-white/10 p-3"><p className="text-xl font-black">{primaryAction ? formatEffort(estimatedMinutes(primaryAction)) : '—'}</p><p className="text-[11px] text-blue-100">effort</p></div>
                      <div className="rounded-2xl bg-white/10 p-3"><p className="text-xl font-black">Élevé</p><p className="text-[11px] text-blue-100">impact</p></div>
                      <div className="rounded-2xl bg-white/10 p-3"><p className="text-xl font-black">{primaryAction ? sourceLabel(primaryAction.source) : '—'}</p><p className="text-[11px] text-blue-100">source</p></div>
                    </div>
                  </div>
                </div>

                <div className="p-5">
                  {primaryAction ? <>
                    <div className="grid gap-3 md:grid-cols-4">
                      <SmallMetric label="Client" value={primaryAction.clientName} />
                      <SmallMetric label="Statut" value={workflowStatusMeta(itemStatuses[primaryAction.id]).label} />
                      <SmallMetric label="Responsable" value={primaryAction.owner || 'À assigner'} />
                      <SmallMetric label="Bloque" value={primaryAction.bucket === 'risk' ? 'Relance / suivi' : 'Avancement client'} />
                    </div>
                    <div className="mt-5 grid gap-4 xl:grid-cols-[minmax(0,1fr)_320px]">
                      <section className="rounded-2xl border border-slate-200 bg-white p-4">
                        <h3 className="text-lg font-black text-slate-950">Étapes de traitement</h3>
                        <p className="text-sm text-slate-500">Le Bureau transforme la priorité en mini workflow exécutable.</p>
                        <div className="mt-3 space-y-2">
                          {[
                            ['1', 'Vérifier le contexte source', primaryAction.signal, 'Voir'],
                            ['2', primaryAction.nextAction, 'Action opérationnelle recommandée.', 'Créer'],
                            ['3', 'Préparer la relance ou le briefing', 'Edith génère le texte à valider.', 'Préparer'],
                            ['4', 'Marquer prêt ou traité', 'Fermer la boucle et passer à la suite.', 'Finaliser'],
                          ].map(([num, title, detail, label], index) => <div key={num} className={`grid grid-cols-[34px_minmax(0,1fr)_auto] items-start gap-3 rounded-2xl border p-3 ${index === 1 ? 'border-blue-200 bg-blue-50' : 'border-slate-100 bg-slate-50'}`}><span className={`flex h-8 w-8 items-center justify-center rounded-xl text-sm font-black ${index === 1 ? 'bg-blue-600 text-white' : 'bg-blue-50 text-blue-700'}`}>{num}</span><div><p className="text-sm font-black text-slate-900">{title}</p><p className="text-xs text-slate-500">{detail}</p></div><button type="button" onClick={() => index === 0 ? openSourceUrl(primaryAction) : index === 1 ? handleAction(primaryAction.actions[0] || { label: 'Ouvrir', kind: 'open-project' as any }, primaryAction) : index === 2 ? openEdithAssistant(advisorContextPrompt('Prépare la relance ou le briefing pour cette action.', primaryAction), primaryAction) : markDone(primaryAction)} className="rounded-xl border border-slate-200 bg-white px-3 py-1.5 text-xs font-black text-slate-700">{label}</button></div>)}
                        </div>
                      </section>
                      <aside className="rounded-2xl border border-blue-100 bg-blue-50 p-4">
                        <h3 className="text-lg font-black text-slate-950">Pourquoi cette action maintenant</h3>
                        <ul className="mt-3 space-y-2 text-sm text-slate-700">{(primaryAction.why.length ? primaryAction.why : [primaryAction.signal]).slice(0, 3).map((reason) => <li key={reason} className="flex gap-2"><CheckCircle className="mt-0.5 h-4 w-4 shrink-0 text-blue-600" />{reason}</li>)}</ul>
                        <div className="mt-4 grid gap-2">
                          {primaryAction.actions[0] && <button type="button" onClick={() => handleAction(primaryAction.actions[0], primaryAction)} className="rounded-xl bg-blue-600 px-3 py-2 text-xs font-black text-white">{primaryAction.actions[0].label}</button>}
                          <button type="button" onClick={() => openSourceUrl(primaryAction)} className="rounded-xl border border-blue-200 bg-white px-3 py-2 text-xs font-black text-blue-800">Ouvrir source</button>
                          <button type="button" onClick={() => markNotToday(primaryAction)} className="rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs font-black text-slate-700">Pas aujourd’hui</button>
                        </div>
                      </aside>
                    </div>
                    <div className="mt-4 rounded-2xl border border-blue-100 bg-blue-50 p-3">
                      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between"><div><p className="font-black text-blue-950">Feuille de route prête</p><p className="text-xs font-semibold text-blue-800">{dailyPlan?.headline || `${dayPlan.highCount} priorités · ${dayPlan.issueCount} corrections · ${dayPlan.briefingCount} validations`}</p></div><div className="flex flex-wrap gap-2"><button type="button" onClick={exportDailyCalendar} className="rounded-xl border border-blue-200 bg-white px-3 py-2 text-xs font-black text-blue-800">Ajouter au calendrier</button><button type="button" onClick={() => openEdithAssistant(advisorContextPrompt('Résume la feuille de route en briefing quotidien.'))} className="rounded-xl bg-blue-600 px-3 py-2 text-xs font-black text-white">Résumer avec Edith</button></div></div>
                    </div>
                  </> : <EmptyState label="Aucune action active avec les filtres actuels." />}
                </div>
              </article>
            </div>

            <aside className="space-y-4">
              <section className="rounded-3xl border border-blue-100 bg-white p-5 shadow-sm">
                <div className="flex items-center gap-3"><div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-blue-100 text-blue-700"><UserRound className="h-5 w-5" /></div><div><h3 className="font-black text-slate-950">Edith contextuelle</h3><p className="text-xs text-slate-500">Liée au client et à l’étape actuelle.</p></div></div>
                <div className="mt-4 grid gap-2">
                  <button type="button" onClick={() => openEdithAssistant(advisorContextPrompt('Prépare la relance après création de tâche.', primaryAction || undefined), primaryAction || undefined)} className="rounded-xl border border-slate-200 px-3 py-2 text-left text-xs font-black text-slate-700 hover:border-blue-300">Prépare la relance après création de tâche</button>
                  <button type="button" onClick={() => openEdithAssistant(advisorContextPrompt('Résume le contexte client en 5 lignes.', primaryAction || undefined), primaryAction || undefined)} className="rounded-xl border border-slate-200 px-3 py-2 text-left text-xs font-black text-slate-700 hover:border-blue-300">Résume le contexte client en 5 lignes</button>
                  <button type="button" onClick={() => openEdithAssistant(advisorContextPrompt('Explique pourquoi cette action est prioritaire.', primaryAction || undefined), primaryAction || undefined)} className="rounded-xl border border-slate-200 px-3 py-2 text-left text-xs font-black text-slate-700 hover:border-blue-300">Explique pourquoi cette action est prioritaire</button>
                </div>
              </section>

              <section className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm">
                <div className="mb-3 flex items-start justify-between gap-3"><div><h3 className="font-black text-slate-950">Suite de la journée</h3><p className="text-xs text-slate-500">Aperçu court après le focus actuel.</p></div><button type="button" onClick={scrollToFullFile} className="rounded-xl border border-slate-200 px-2 py-1 text-xs font-black text-slate-700 hover:border-blue-300">Toute la file</button></div>
                <div className="space-y-2">{nextDayItems.length ? nextDayItems.map((item) => <div key={item.id} className="rounded-2xl border border-slate-100 bg-slate-50 p-3"><div className="flex items-start justify-between gap-2"><div><p className="text-sm font-black text-slate-900">{item.nextAction}</p><p className="text-xs text-slate-500">{item.clientName} · {sourceLabel(item.source)}</p></div><span className={`rounded-full px-2 py-1 text-[10px] font-black ${item.priority === 'high' ? 'bg-red-50 text-red-700' : 'bg-amber-50 text-amber-700'}`}>{item.priority === 'high' ? 'À faire' : 'À valider'}</span></div><button type="button" onClick={() => { setSelectedItemId(item.id); setContextDrawerItem(item); }} className="mt-2 rounded-full border border-blue-100 bg-blue-50 px-2 py-1 text-[11px] font-black text-blue-700">Contexte</button></div>) : <EmptyState label="Rien d’autre dans la suite de journée." />}</div>
              </section>

              <section aria-label="Gestion quotidienne repliée" className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm">
                <div className="mb-3"><h3 className="font-black text-slate-950">Gestion quotidienne</h3><p className="text-xs text-slate-500">Attentes, inbox et sources restent disponibles sans voler le focus.</p></div>
                <div className="mb-3 grid grid-cols-3 gap-1 rounded-2xl bg-slate-100 p-1">
                  {(['attentes', 'inbox', 'sources'] as const).map((tab) => <button key={tab} type="button" onClick={() => setManagementTab(tab)} className={`rounded-xl px-2 py-1.5 text-xs font-black ${managementTab === tab ? 'bg-white text-slate-950 shadow-sm' : 'text-slate-500'}`}>{tab === 'attentes' ? 'Attentes' : tab === 'inbox' ? 'Inbox' : 'Sources'}</button>)}
                </div>
                {managementTab === 'attentes' && <div className="space-y-2">{(waitingItems.length ? waitingItems : visibleItems.slice(0, 2)).map((item) => <div key={item.id} className="rounded-2xl border border-slate-100 bg-slate-50 p-3"><p className="text-sm font-black text-slate-900">{item.clientName}</p><p className="text-xs text-slate-500">En attente · prochaine relance prévue {nextWeekIso()}</p><div className="mt-2 flex gap-2"><button type="button" onClick={() => openEdithAssistant(advisorContextPrompt('Prépare une relance courte.', item), item)} className="rounded-full bg-blue-50 px-2 py-1 text-[11px] font-black text-blue-700">Préparer relance</button><button type="button" onClick={() => markNotToday(item)} className="rounded-full bg-white px-2 py-1 text-[11px] font-black text-slate-700">Pas aujourd’hui</button></div></div>)}</div>}
                {managementTab === 'inbox' && <div className="space-y-2">{dataIssues.slice(0, 3).map((issue) => <div key={issue.id} className="rounded-2xl border border-slate-100 bg-slate-50 p-3"><p className="text-sm font-black text-slate-900">{issue.label}</p><p className="text-xs text-slate-500">{issue.detail}</p><div className="mt-2 flex gap-2"><button type="button" onClick={() => handleCorrectionAction(issue)} className="rounded-full bg-blue-50 px-2 py-1 text-[11px] font-black text-blue-700">Traiter</button><button type="button" onClick={() => issue.item ? markNotToday(issue.item) : addHistory('Blocage reporté', undefined, issue.label)} className="rounded-full bg-white px-2 py-1 text-[11px] font-black text-slate-700">Plus tard</button></div></div>)}</div>}
                {managementTab === 'sources' && <div><div className="space-y-1">{sourceHealth.map((source) => <div key={source.source} className="flex items-center justify-between border-t border-slate-100 py-1.5 text-xs"><span className="font-bold text-slate-600">{source.label}</span><span className={source.loaded ? 'font-black text-emerald-700' : 'font-black text-red-700'}>{source.loaded ? 'OK' : 'Partiel'}</span></div>)}</div><p className="mt-2 text-[11px] text-slate-500">Santé des sources ultra discrète : ouvrir seulement si une recommandation semble incomplète.</p></div>}
              </section>
            </aside>
          </section>

          {viewMode !== 'actions' && <section ref={fileSectionRef} className="mt-5 rounded-3xl border border-slate-200 bg-white p-5 shadow-sm">
            <div className="mb-4 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between"><div><h3 className="text-lg font-black text-slate-950">{viewMode === 'assigner' ? 'Dossiers à assigner' : 'Toute la file'}</h3><p className="text-sm text-slate-500">{viewMode === 'assigner' ? 'Items sans responsable ou référent. Ouvre la source ou le contexte avant correction.' : 'File complète selon l’horizon et les filtres actifs. Cliquer une ligne met à jour l’action actuelle.'}</p></div><button type="button" onClick={scrollToFocus} className="rounded-xl border border-slate-200 px-3 py-2 text-xs font-black text-slate-700">Revenir au focus</button></div>
            <div className="grid gap-3 xl:grid-cols-2">{(viewMode === 'assigner' ? assignableItems : visibleItems).length ? (viewMode === 'assigner' ? assignableItems : visibleItems).slice(0, 24).map((item, index) => renderWorkItem(item, index, viewMode === 'assigner' ? 'À assigner' : horizonOptions.find((option) => option.key === horizonFilter)?.label)) : <EmptyState label="Aucun dossier dans cette vue." />}</div>
          </section>}
        </main>
      </div>

      {contextItem && contextDrawerItem && <div className="fixed inset-0 z-[60] flex justify-end bg-slate-950/50 backdrop-blur-sm" onClick={() => setContextDrawerItem(null)}>
        <aside className="h-full w-full max-w-xl overflow-y-auto border-l border-slate-200 bg-slate-50 p-5 shadow-2xl" onClick={(event) => event.stopPropagation()}>
          <div className="mb-4 flex items-start justify-between gap-3"><div><p className="text-xs font-black uppercase tracking-[0.18em] text-blue-700">Client 360</p><h3 className="text-2xl font-black text-slate-950">{contextItem.clientName}</h3><p className="text-sm text-slate-500">Contexte immédiat lié à l’action sélectionnée.</p></div><button type="button" onClick={() => setContextDrawerItem(null)} className="rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs font-black text-slate-700">Fermer</button></div>
          <section className="rounded-2xl border border-blue-100 bg-blue-50 p-4"><h4 className="font-black text-blue-950">À savoir avant d’agir</h4><ul className="mt-2 space-y-2 text-sm text-slate-700">{(contextItem.why.length ? contextItem.why : [contextItem.signal]).slice(0, 4).map((reason) => <li key={reason} className="flex gap-2"><CheckCircle className="mt-0.5 h-4 w-4 shrink-0 text-blue-600" />{reason}</li>)}</ul></section>
          <section className="mt-3 rounded-2xl border border-slate-200 bg-white p-4"><h4 className="font-black text-slate-950">Contexte récent</h4><p className="mt-2 text-sm text-slate-600">Dernier suivi source : {contextItem.statusLabel || 'à préparer'} · responsable : {contextItem.owner || 'à assigner'}.</p><div className="mt-3 rounded-2xl border border-amber-100 bg-amber-50 p-3"><p className="text-sm font-black text-amber-900">Prochaine relance prévue</p><p className="text-xs font-semibold text-amber-800">{nextWeekIso()} si aucune réponse après le briefing court.</p></div></section>
          <section className="mt-3 rounded-2xl border border-slate-200 bg-white p-4"><h4 className="font-black text-slate-950">Dernières décisions</h4><div className="mt-3 space-y-2">{shownDecisions.map((event) => <div key={event.id} className="rounded-xl border border-slate-100 bg-slate-50 p-3"><p className="text-sm font-black text-slate-800">{event.label}</p><p className="text-xs text-slate-500">{event.detail || new Date(event.at).toLocaleString('fr-CA')}</p></div>)}</div></section>
          <div className="mt-3 grid gap-2 sm:grid-cols-2"><SmallMetric label="Source" value={sourceLabel(contextItem.source)} /><SmallMetric label="Projet" value={contextItem.actions.some((action) => action.href?.includes('/projets-v2')) ? 'Trouvé' : 'À valider'} /><SmallMetric label="Document" value={contextItem.bucket === 'documents' ? 'Lié' : 'Rapport mensuel'} /><SmallMetric label="Statut" value={workflowStatusMeta(itemStatuses[contextItem.id]).label} /></div>
          <label className="mt-4 block text-xs font-black uppercase tracking-wide text-slate-500">Note rapide</label><textarea disabled={!canEditWorkflow} value={notesByClient[contextItem.clientName] || ''} onChange={(event) => { const next = { ...notesByClient, [contextItem.clientName]: event.target.value }; setNotesByClient(next); void persistStatePatch({ notesByClient: next }); }} placeholder="Décision, contexte client, prochaine relance..." className="mt-2 min-h-[88px] w-full rounded-xl border border-slate-200 bg-white p-3 text-sm text-slate-700 outline-none focus:border-blue-300" />
          <div className="mt-4 grid gap-2 sm:grid-cols-2">{contextItem.actions[0] && <button type="button" onClick={() => handleAction(contextItem.actions[0], contextItem)} className="rounded-xl bg-blue-600 px-3 py-2 text-xs font-black text-white sm:col-span-2">{contextItem.actions[0].label}</button>}<button type="button" onClick={() => openSourceUrl(contextItem)} className="rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs font-black text-slate-700">Ouvrir source</button><button type="button" onClick={() => markNotToday(contextItem)} className="rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs font-black text-slate-700">Pas aujourd’hui</button><button type="button" disabled={!canEditWorkflow} onClick={() => markDone(contextItem)} className="rounded-xl bg-emerald-600 px-3 py-2 text-xs font-black text-white sm:col-span-2">Marquer traité</button></div>
        </aside>
      </div>}

      {dayPlanningOverlayOpen && <div className="fixed inset-0 z-[70] flex items-center justify-center bg-slate-950/60 p-4 backdrop-blur-sm" onClick={() => setDayPlanningOverlayOpen(false)}>
        <section className="max-h-[92vh] w-full max-w-5xl overflow-y-auto rounded-3xl border border-slate-200 bg-slate-50 shadow-2xl" onClick={(event) => event.stopPropagation()}>
          <header className="sticky top-0 z-10 flex items-start justify-between gap-4 border-b border-slate-200 bg-white/95 p-5 backdrop-blur"><div><p className="text-xs font-black uppercase tracking-[0.18em] text-blue-700">Planifier ma journée</p><h3 className="text-2xl font-black text-slate-950">Plan proposé par PRIsME — à approuver</h3><p className="text-sm text-slate-500">Rien n’est appliqué tant que le conseiller n’a pas confirmé. Ajuste ordre, durée, reports et briefing.</p></div><button type="button" onClick={() => setDayPlanningOverlayOpen(false)} className="rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs font-black text-slate-700">Fermer</button></header>
          <div className="space-y-4 p-5">
            <div className="grid gap-3 md:grid-cols-4">{[['État', 'À approuver'], ['Charge prévue', formatEffort(draftEstimatedMinutes || totalEstimatedMinutes)], ['Blocs au plan', String(scheduledDraftItems.length)], ['Reports/retirés', String(dayPlanDraftRows.filter((row) => row.decision !== 'scheduled').length)]].map(([label, value]) => <SmallMetric key={label} label={label} value={value} />)}</div>
            <div className="grid gap-4 xl:grid-cols-[minmax(0,1.15fr)_0.85fr]">
              <section className="rounded-2xl border border-slate-200 bg-white p-4">
                <h4 className="font-black text-slate-950">Plan modifiable</h4>
                <p className="text-sm text-slate-500">Les boutons modifient le brouillon visible seulement. Rien n’est appliqué avant l’approbation.</p>
                <div className="mt-3 space-y-3">
                  {dayPlanDraftItems.length ? dayPlanDraftItems.map(({ item, row }) => {
                    const block = draftCalendarBlocks.find((candidate) => candidate.itemId === item.id);
                    const isScheduled = row.decision === 'scheduled';
                    return (
                      <div
                        key={item.id}
                        data-day-plan-draft-row
                        className={`rounded-2xl border p-3 ${isScheduled ? 'border-slate-100 bg-slate-50' : 'border-amber-100 bg-amber-50'}`}
                      >
                        <div className="grid gap-3 md:grid-cols-[72px_minmax(0,1fr)]">
                          <div className="text-xs font-black text-blue-700">
                            {isScheduled ? block?.startTime || '—' : row.decision === 'tomorrow' ? 'Demain' : 'Retiré'}
                          </div>
                          <div className="min-w-0 space-y-3">
                            <div>
                              <p className="break-words text-sm font-black leading-5 text-slate-900">{item.nextAction}</p>
                              <p className="mt-1 text-xs text-slate-500">{item.clientName} · {sourceLabel(item.source)}</p>
                              {row.decision !== 'scheduled' && (
                                <p className="mt-1 text-[11px] font-black text-amber-800">
                                  {row.decision === 'tomorrow' ? 'Sera reporté demain à l’approbation.' : 'Ne sera pas inclus dans ce plan.'}
                                </p>
                              )}
                            </div>
                            <div className="grid gap-2 sm:grid-cols-[minmax(0,1fr)_120px] sm:items-start">
                              <div data-day-plan-actions className="flex flex-wrap gap-2">
                                <button type="button" title="Monter cette action" onClick={() => moveDayPlanDraftRow(item.id, -1)} className="rounded-lg border bg-white px-3 py-2 text-xs font-black">Monter</button>
                                <button type="button" title="Descendre cette action" onClick={() => moveDayPlanDraftRow(item.id, 1)} className="rounded-lg border bg-white px-3 py-2 text-xs font-black">Descendre</button>
                                {row.decision === 'scheduled' ? (
                                  <>
                                    <button type="button" onClick={() => updateDayPlanDraftDecision(item.id, 'tomorrow')} className="rounded-lg border bg-white px-3 py-2 text-xs font-black">Reporter</button>
                                    <button type="button" onClick={() => updateDayPlanDraftDecision(item.id, 'removed')} className="rounded-lg border bg-white px-3 py-2 text-xs font-black">Retirer</button>
                                  </>
                                ) : (
                                  <button type="button" onClick={() => updateDayPlanDraftDecision(item.id, 'scheduled')} className="rounded-lg border bg-white px-3 py-2 text-xs font-black">Remettre</button>
                                )}
                              </div>
                              <select
                                aria-label={`Durée prévue pour ${item.clientName}`}
                                disabled={!isScheduled}
                                value={row.durationMinutes}
                                onChange={(event) => updateDayPlanDraftDuration(item.id, Number(event.target.value))}
                                className="w-full rounded-xl border border-slate-200 bg-white px-2 py-2 text-xs font-bold disabled:opacity-50"
                              >
                                <option value={15}>15 min</option>
                                <option value={20}>20 min</option>
                                <option value={30}>30 min</option>
                                <option value={45}>45 min</option>
                                <option value={60}>60 min</option>
                                <option value={90}>90 min</option>
                              </select>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  }) : <EmptyState label="Aucun bloc proposé." />}
                </div>
              </section>
              <section className="space-y-3"><div className="rounded-2xl border border-blue-100 bg-blue-50 p-4"><h4 className="font-black text-blue-950">Résumé recalculé</h4><p className="mt-2 text-sm text-blue-800">{draftSummary}. Premier bloc : {scheduledDraftItems[0]?.item.clientName || '—'} · {scheduledDraftItems[0]?.item.nextAction || '—'}.</p></div><div className="rounded-2xl border border-slate-200 bg-white p-4"><h4 className="font-black text-slate-950">Calendrier</h4><label className="mt-2 flex gap-2 text-sm"><input type="checkbox" checked={draftIncludeFocus} onChange={(event) => setDraftIncludeFocus(event.target.checked)} /> Inclure les blocs focus</label><label className="mt-2 flex gap-2 text-sm"><input type="checkbox" checked={draftIncludeRelances} onChange={(event) => setDraftIncludeRelances(event.target.checked)} /> Inclure les relances client</label><label className="mt-2 flex gap-2 text-sm"><input type="checkbox" checked={draftIncludeCorrections} onChange={(event) => setDraftIncludeCorrections(event.target.checked)} /> Inclure les corrections mineures</label><p className="mt-3 text-xs text-slate-500">Ces options ajustent ce qui sera marqué au statut conseiller lors de l’approbation; elles ne touchent pas aux systèmes externes.</p></div></section>
            </div>
            <section className="rounded-2xl border border-slate-200 bg-white p-4"><h4 className="font-black text-slate-950">Briefing quotidien modifiable</h4><textarea value={draftBriefingBody} onChange={(event) => setDraftBriefingBody(event.target.value)} className="mt-3 min-h-[150px] w-full rounded-xl border border-blue-100 bg-white p-3 text-sm text-slate-700" /></section>
            <section className="rounded-2xl border border-purple-100 bg-purple-50 p-4"><h4 className="font-black text-purple-950">Pourquoi cet ordre</h4><div className="mt-2 space-y-2">{buildPriorityRationale(scheduledDraftItems.map(({ item }) => item)).map((reason) => <p key={reason} className="rounded-xl bg-white p-2 text-xs font-semibold text-purple-900">{reason}</p>)}</div></section>
          </div>
          <footer className="flex flex-wrap justify-end gap-2 border-t border-slate-200 bg-white p-4"><button type="button" onClick={() => setDayPlanningOverlayOpen(false)} className="rounded-xl border border-slate-200 px-3 py-2 text-xs font-black text-slate-700">Annuler</button><button type="button" onClick={saveDayPlanDraft} className="rounded-xl border border-slate-200 px-3 py-2 text-xs font-black text-slate-700">Enregistrer brouillon</button><button type="button" onClick={prepareMyDay} disabled={!scheduledDraftItems.length} className="rounded-xl bg-blue-600 px-3 py-2 text-xs font-black text-white disabled:cursor-not-allowed disabled:opacity-50">Appliquer / approuver ma journée</button></footer>
        </section>
      </div>}

      <AIAssistantModal
        isOpen={isEdithOpen}
        onClose={() => setIsEdithOpen(false)}
        initialQuestion={edithQuestion}
      />
    </div>
  );
}

function FlagIcon() {
  return <AlertTriangle className="h-5 w-5 text-red-600" />;
}

function EmptyState({ label }: { label: string }) {
  return <div className="rounded-xl border border-dashed border-slate-200 bg-slate-50 p-3 text-sm text-slate-500">{label}</div>;
}

function InfoPanel({ title, icon: Icon, children }: { title: string; icon: typeof CalendarCheck; children: ReactNode }) {
  return <section className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm"><div className="mb-3 flex items-center gap-2"><Icon className="h-5 w-5 text-blue-600" /><h3 className="font-black text-slate-950">{title}</h3></div><div className="space-y-2">{children}</div></section>;
}

function SmallActionRow({ title, detail, meta, onClick }: { title: string; detail: string; meta?: string; onClick?: () => void }) {
  return <button type="button" onClick={onClick} className="flex w-full items-center justify-between gap-3 rounded-xl border border-slate-100 bg-slate-50 p-3 text-left hover:border-blue-200 hover:bg-blue-50"><div className="min-w-0"><p className="truncate text-sm font-bold text-slate-800">{title}</p><p className="truncate text-xs text-slate-500">{detail}</p></div>{meta && <span className="shrink-0 text-xs font-bold text-blue-700">{meta}</span>}<ChevronRight className="h-4 w-4 text-slate-300" /></button>;
}

function SmallMetric({ label, value, positive = false }: { label: string; value: string; positive?: boolean }) {
  return <div className="flex items-center justify-between rounded-xl bg-slate-50 px-3 py-2 text-sm"><span className="text-slate-600">{label}</span><strong className={positive ? 'text-emerald-700' : 'text-slate-950'}>{value}</strong></div>;
}
